import { createContext, lazy, memo, Suspense, useCallback, useContext, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useLocation, useNavigate } from 'react-router-dom'
import { useRoomContext } from '@livekit/components-react'
import { Eye, UserRoundX } from 'lucide-react'
import { LayoutAudioGate } from '@/components/event-viewer/LayoutAudioGate'
import { EventLiveKitAudioRenderer } from '@/components/livekit/EventLiveKitProvider'
import { cameraFrame, focusCamera } from '@/components/map/camera-sphere'
import { CourseMap, type CourseMapCamera } from '@/components/map/course-map'
import type { CourseMapHostView } from '@/components/map/course-map/CourseMap'
import { courseKm } from '@/components/map/course-map/courses'
import { locatedCameras } from '@/components/map/course-map/locatedCameras'
import { Badge, Button, ButtonLink, ConfirmDialog, EmptyState, Notice, Skeleton, SkeletonGroup, toast } from '@/components/play'
import { SiteFooter } from '@/components/SiteFooter'
import { SiteHeader, type SiteEventLogo } from '@/components/SiteHeader'
import type { BeforeLeave } from '@/components/siteLeave'
import { CameraCheckSheet, type CameraCheckChoice, type CameraCheckSheetProps } from '@/components/watch/CameraCheckSheet'
import { BroadcastLayer, GraphicsConsoleOpener, rosterById, type BroadcastContext, type GraphicPreview } from '@/components/watch/broadcast'
import { CameraStrip } from '@/components/watch/CameraStrip'
import { ChatHead, type ChatHeadProps } from '@/components/watch/ChatHead'
import { ChatPanel, type ChatInvite } from '@/components/watch/ChatPanel'
import { EventDetailsSheet } from '@/components/watch/EventDetailsSheet'
import { FullscreenToggle } from '@/components/watch/FullscreenToggle'
import { GuestDock, leaveFailureMessage } from '@/components/watch/GuestDock'
import { endFailureMessage, HostDock, PreviewLabel, type HostDockProps } from '@/components/watch/HostDock'
import { FollowHost, HostMapLive } from '@/components/watch/HostMap'
import { inviteOnStage } from '@/components/watch/HostInvites'
import { LiveTrack } from '@/components/watch/LiveTrack'
import { MapLoading, MapWaiting, NoCourseMap } from '@/components/watch/MapStates'
import { MovableSlot } from '@/components/watch/Movable'
import { Audience } from '@/components/watch/Audience'
import { ReactionBar, ReactionLayer } from '@/components/watch/Reactions'
import { StagePulseProvider } from '@/lib/watch/stagePulse'
import { cameraCountOf, connectionWait, type CameraCount } from '@/components/watch/noCameras'
import { PhoneTabs, type LandscapeView } from '@/components/watch/PhoneTabs'
import { SignInSheet } from '@/components/watch/SignInSheet'
import { WatchSound } from '@/components/watch/SoundControl'
import { StageCard, type StageCardAnswer } from '@/components/watch/StageCard'
import { StageButton, type StageButtonProps } from '@/components/watch/StageButton'
import { StageFaces } from '@/components/watch/StageFaces'
import { StageSwitcher } from '@/components/watch/StageSwitcher'
import { UsernameStep } from '@/components/watch/UsernameStep'
import { WatchBoundary } from '@/components/watch/WatchBoundary'
import { jumpOrigin, swapCardOver, WatchCanvas, type CanvasConnection, type CanvasJump } from '@/components/watch/WatchCanvas'
import { WatchInfo, type WatchInfoProps } from '@/components/watch/WatchInfo'
import { WatchRoom, type WatchRoomStatus } from '@/components/watch/WatchRoom'
import { useWatchingStage } from '@/lib/watch/useWatchingStage'
import { WatchSheet } from '@/components/watch/WatchSheet'
import { apiFailure } from '@/lib/apiFailure'
import { isMappable, type ViewerPayload } from '@/lib/comrades/viewerPayload'
import type { WorldElement } from '@/lib/comrades/worldLayer'
import { isValidRouteBounds } from '@/lib/map/routeFocus'
import { watchApi } from '@/lib/watch/api'
import { invitesApi, type ChatPerson } from '@/lib/watch/invitesApi'
import { clearIntent, peekIntent, takeIntent, type WatchIntent } from '@/lib/watch/intent'
import { retryDelayMs } from '@/lib/watch/reconnect'
import {
  chatBody,
  formatAudience,
  formatCourseLength,
  formatCourseRange,
  isChatCard,
  mapSource,
  nameOrHandle,
  profileAvatarUrl,
  sameHandle,
  sentenceName,
  stageFull,
  stageLabel,
  stagePath,
  type ChatCardMessage,
  type EventPhase,
} from '@/lib/watch/rules'
import type { ChatMessage, PublicProfile, StageFace, StageInfo, StagePayload, WatchCamera } from '@/lib/watch/types'
import { useEventStages, type EventStage } from '@/lib/watch/useEventStages'
import { useEventPhase } from '@/lib/watch/useEventPhase'
import { preShowEligible } from '@/lib/watch/preShow'
import { usePreShowStep } from '@/lib/watch/usePreShowStep'
import { useRegistrations } from '@/lib/watch/useRegistrations'

import { canGoFullscreen, leaveFullscreen, useFullscreen } from '@/lib/watch/useFullscreen'
import { useInRoom, useInRoomCount } from '@/lib/watch/useInRoom'
import { useLiveViewerPayload } from '@/lib/watch/useLiveViewerPayload'
import { useOnline } from '@/lib/watch/useOnline'
import { PHONE_QUERY, useHeld, useMediaQuery, useTypingOnScreen, useWatchLayout, type WatchLayout } from '@/lib/watch/useMediaQuery'
import { useMovableNode } from '@/lib/watch/useMovableNode'
import { useViewerVolume } from '@/lib/watch/useViewerVolume'
import { useSourceAudio } from '@/lib/watch/useSourceAudio'
import { useSourceVolume } from '@/lib/watch/useSourceVolume'
import { useVisibleArea } from '@/lib/watch/useVisibleArea'
import { usePhoneTab } from '@/lib/watch/usePhoneTab'
import { useSiteLinks } from '@/lib/watch/useSiteLinks'
import { useStageChat, type SignInReason } from '@/lib/watch/useStageChat'
import { useHostSources } from '@/lib/watch/useHostSources'
import { StagePresenceFailure, useStagePresence } from '@/lib/watch/useStagePresence'
import { useAudience, useWatchScreen, type WatchScreen as WatchScreenState } from '@/lib/watch/useWatchScreen'
import { useHostMapView, type HostMapView } from '@/lib/watch/useHostMapView'
import type { WatchTokenError } from '@/lib/watch/useWatchToken'
import { useWorldLayer } from '@/lib/watch/useWorldLayer'
import { useEventAthletes, type EventAthletes } from '@/lib/watch/eventAthletes'
import { eventRaces } from '@/lib/watch/races'
import { useStageActions } from '@/lib/watch/stageActions'
import { useCohostConsole } from '@/lib/watch/cohostActions'
import { useCohostHandlers, type CohostDone } from '@/lib/watch/cohostHandlers'
import { useCohostIdentity } from '@/lib/watch/useCohost'
import { NO_VISIBLE_GRAPHICS, useStageGraphics } from '@/lib/watch/useStageGraphics'
import { useAuth } from '@/state/AuthContext'
import '@/components/watch/watch.css'

// The pre-show's globe, d3 and the world's outlines load only for a page that shows it.
const PreShow = lazy(() => import('@/components/watch/preShow/PreShow').then((module) => ({ default: module.PreShow })))

const LOAD_FAILED = 'We couldn’t load this event. Try again.'
const LOAD_FAILED_HEADING = 'We couldn’t load this event'
const STAGE_UNAVAILABLE = 'This event’s stage isn’t available yet.'
const CONNECT_FAILED = 'We couldn’t connect. Trying again…'
const BUSY = 'It’s busy right now. Trying again…'
const VIDEO_UNAVAILABLE = 'We couldn’t open this event’s video.'
const MAP_FAILED = 'We couldn’t load the map.'
const CHAT_FAILED = 'We couldn’t load the chat.'

/**
 * What to do once the viewer is signed in with a username: send the chat (the draft, or an intent's text), open the
 * host's camera check, or open a guest's (their invite's Join stage).
 */
type Pending = { kind: 'chat'; draft?: string } | { kind: 'go_live' } | { kind: 'join_stage' }

/**
 * The viewer's latest invite card on the stage, and the latest card about them (invited, joined, left): what says
 * their invitation may have changed, so the page asks the server again.
 */
function cardsAbout(messages: ChatMessage[], viewerId: string | null) {
  let invite: ChatCardMessage | null = null
  let about: string | null = null
  if (viewerId === null) return { invite, about }
  for (const message of messages) {
    if (!isChatCard(message) || message.subject.id !== viewerId) continue
    about = message.id
    if (message.kind === 'invite') invite = message
  }
  return { invite, about }
}

/** A guest taken off the stage hears it quietly; a stage that dropped them for a fault says so as an error. */
const QUIET_DROPS = new Set(['left_stage', 'seat_lost', 'live_ended'])

/**
 * Why the stage isn't on screen: it failed to load (Try again), the API has none for the event yet (a 404), or the
 * URL's handle has no stage on the event.
 */
type StageFailure = 'failed' | 'unavailable' | 'not_found'

/** The event has a course to draw: a route the map can load (by id or URL) or frame (by its bounds). */
function hasCourse(route: ViewerPayload['route']): boolean {
  return Boolean(route && (route.id || route.gpx_url || isValidRouteBounds(route.bounds)))
}

/** True once `now` has been, from then on: what the layout was given for the event isn't taken away again. */
function useLatched(now: boolean): boolean {
  const [latched, setLatched] = useState(now)
  if (now && !latched) setLatched(true)
  return latched || now
}

/**
 * The event has something to map: a course, or a camera with a location (live or not). From the payload, not from who
 * is live: on a `live_only` event without a route the cameras come and go from the strip as they drop and return (and
 * all of them while the room connects), and the map must not come and go with them (each mount is a billed Mapbox load,
 * and the swap mode would reset). Once true it stays true.
 */
function useHasMap(payload: ViewerPayload): boolean {
  const located = payload.cameras.some((camera) => camera.participant_identity && isMappable(camera))
  return useLatched(hasCourse(payload.route) || located)
}

/**
 * The event has more than one source for the strip to list beside the one on the canvas (the strip leaves the watched
 * one out): its cameras and Live shows, offline ones included, and the screens hosts share, so the desktop keeps the
 * strip's room under the canvas. A single source would only ever leave the room
 * blank. From the payload, as `useHasMap`: a `live_only` event's strip empties while the room connects, on a full
 * reconnect (LiveKit clears the remote participants) and when its last camera drops, and a canvas sized by the column's
 * height would jump each time. Once true it stays true.
 */
function useHasStrip({ cameras, shows }: CameraCount, hostSources: number): boolean {
  return useLatched(cameras + shows + hostSources > 1)
}

/** A live host's own picture (a shared screen; a Studio face is never in the list) rather than one of the event's cameras: never on the map. */
const isHostSource = (camera: WatchCamera) => camera.sourceRole === 'studio' || camera.sourceRole === 'screen'

/**
 * Nothing for the map to frame: no course, and no located camera among those shown (a `live_only` event without a
 * route whose located cameras are all offline). Mapbox would show its default view, an empty piece of the world with no
 * sphere.
 */
function unframedMap(route: ViewerPayload['route'], cameras: WatchCamera[]): boolean {
  if (hasCourse(route)) return false
  return locatedCameras(cameras.map((camera) => ({ coordinate: camera.location }))).length === 0
}

/** The map's course while it has nothing to frame: mounted and laid out, hidden from sight (watch.css) and assistive tech. */
const UNFRAMED = { 'aria-hidden': true, inert: '' } as const

/**
 * The viewer's connection as the map says it while it isn't up (no camera shows live meanwhile): the canvas's words,
 * and Unavailable once the event's video was refused (the page's notice says why).
 */
function mapConnectionStatus(connection: CanvasConnection) {
  if (connection === 'connected') return undefined
  return connection === 'stopped' ? 'Unavailable' : connectionWait(connection).words
}

/** A sphere's live video. Called inline by CourseMap, so it returns an element and calls no hooks. */
function renderSphereVideo(camera: CourseMapCamera, fallback: ReactNode) {
  return <LiveTrack identity={camera.identity} className="lv-sphere-video" fallback={fallback} />
}

function useDocumentTitle(title: string) {
  useEffect(() => {
    const previous = document.title
    document.title = title
    return () => {
      document.title = previous
    }
  }, [title])
}

/**
 * While live, closing or reloading the tab asks first (the browser shows its own words): leaving ends the live with no
 * End confirmation, and the seat waits for the API's cleanup. The site's links and Sign out ask "End your live?" in the
 * page instead (`useSiteLinks`); react-router's `useBlocker` needs a data router, which the app doesn't use.
 */
function useWarnBeforeUnload(active: boolean) {
  useEffect(() => {
    if (!active) return
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      // Chrome and Edge before 119 ask only when `returnValue` is set.
      event.returnValue = true
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [active])
}

/**
 * Back from Google or Strava without signing in (Back at the provider): the screen comes up signed out with the intent
 * this tab left with still saved. It is abandoned, so a later sign-in mustn't complete it. Checked once, when the
 * session is first known; an intent another tab is signing in for (an emailed link) stays.
 */
function useForgetAbandonedIntent(eventId: string) {
  const { user, authReady } = useAuth()
  const checked = useRef(false)
  useEffect(() => {
    if (checked.current || !authReady) return
    checked.current = true
    if (!user && peekIntent(eventId)?.leftFromHere) clearIntent()
  }, [authReady, user, eventId])
}

/** Which stage a URL's handle names, for the stage cache: '' is the owner's stage, else the handle in lower case. */
const stageKey = (handle: string | null) => (handle ? handle.toLowerCase() : '')

/** Asks to show a stage: the page primes it as loaded (`payload`, viewer unknown until it loads) and goes to `path`. */
export interface StageRequest {
  /** The person's handle for their stage; null for the owner's. */
  handle: string | null
  /**
   * The stage as the switcher has it, shown at once (no viewer yet: what this account may do there loads next); without
   * it the stage on screen stays until it loads.
   */
  payload?: StagePayload
  path: string
  /** Replaces this page in the history (a stage that's gone), rather than adding one. */
  replace?: boolean
}

/**
 * The watch screen (docs/features/watch-experience.md), inside the site's header and footer (`WatchShell`, which the
 * route renders around it). Desktop: the event's bar, the canvas with the swap card, the host's dock and the camera
 * strip on the left, the stage's chat on the right, the site's footer below. Phone: the video first, the site's header
 * floating over its top edge, then the info line (the event's name and meta, the dock), then one panel (Cameras, Map or
 * Chat) chosen in a floating switcher, the footer at the end of the Cameras panel. Phone on its side: the canvas fills
 * the screen (no header or footer) and the chat rises over it. It loads the stage, again whenever the session changes
 * (signing in brings the viewer's own capabilities), and joins the event's room with a watch token.
 *
 * `handle` is the stage the URL names (`/e/:shortcode/@handle`; null for the owner's, `/e/:shortcode`). Switching stage
 * changes the URL (the stage's `path`) and this prop: the room, the map and the video stay (one room per event), the
 * switcher's copy of the stage shows at once and the stage loads with what this account may do on it. A handle with no
 * stage on the event says so with a way to the main stage; after another stage was on screen, it goes to the main stage
 * instead, saying why, so the room stays.
 */
export function WatchScreenPage({ payload: loaded, handle = null }: { payload: ViewerPayload; handle?: string | null }) {
  // The event's cameras and its courses' worlds, kept current over the event's public channel: a camera added after the
  // page loaded shows without a reload, and a rebuilt world swaps in. Everything below reads this payload.
  const payload = useLiveViewerPayload(loaded.event.id, loaded)
  const { event } = payload
  useDocumentTitle(event.name)
  useForgetAbandonedIntent(event.id)
  const navigate = useNavigate()
  const { pathname, search } = useLocation()
  // The event's shortcode, for its stages' paths: the payload's, else the URL's (/e/<shortcode>/…).
  const shortcode = event.shortcode || pathname.split('/')[2] || event.id
  const { token, user, authReady } = useAuth()
  const key = stageKey(handle)
  // Each stage loaded (or primed by the switcher) for this event, by `stageKey`: switching back shows it at once.
  const [cache, setCache] = useState<{ eventId: string; stages: Record<string, StagePayload> }>({ eventId: event.id, stages: {} })
  const cached = cache.eventId === event.id ? cache.stages : null
  const [failure, setFailure] = useState<{ eventId: string; key: string; kind: StageFailure } | null>(null)
  const [attempt, setAttempt] = useState(0)
  // The stages of this event that have been on screen, read by a load that fails: a reload retries quietly, a first
  // load says so.
  const shownFor = useRef<{ eventId: string; keys: Set<string> }>({ eventId: event.id, keys: new Set() })
  if (shownFor.current.eventId !== event.id) shownFor.current = { eventId: event.id, keys: new Set() }

  // A copy primed by the switcher doesn't replace what loaded for the same stage (the loaded one says what this account
  // may do there); it does replace another stage under the same handle (a host removed and invited again).
  const remember = useCallback(
    (forEvent: string, forKey: string, stage: StagePayload, prime = false) =>
      setCache((current) => {
        const stages = current.eventId === forEvent ? current.stages : {}
        if (prime && stages[forKey]?.stage.id === stage.stage.id) return current
        return { eventId: forEvent, stages: { ...stages, [forKey]: stage } }
      }),
    [],
  )
  // Stages the event no longer has (their hosts removed) leave the cache, so a new stage under the same handle loads.
  const forgetStages = useCallback(
    (ids: ReadonlySet<string>) =>
      setCache((current) => {
        const kept = Object.entries(current.stages).filter(([, stage]) => !ids.has(stage.stage.id))
        return kept.length === Object.keys(current.stages).length ? current : { ...current, stages: Object.fromEntries(kept) }
      }),
    [],
  )
  // Something of this event is on screen (the stage loaded, primed, or held while another loads), for a load that fails.
  const onScreenRef = useRef(false)

  // After the session is known, so the stage answers for this account. With a stage on screen meanwhile (a reload, the
  // switcher's copy, or the last stage held while this one loads), a failure retries quietly after 2, 5, 15, then
  // every 30 s plus jitter, as the watch token does; so does a stage the API doesn't have yet (a 404 while a deploy
  // rolls out). A first load with nothing on screen says it failed. A handle with no stage (`stage_not_found`, or an API
  // from before handles answering the owner's stage) is not found, and isn't asked again.
  useEffect(() => {
    if (!authReady) return
    let cancelled = false
    let failures = 0
    let retry: ReturnType<typeof setTimeout> | null = null
    const shown = shownFor.current
    const load = () => {
      retry = null
      const request = handle ? watchApi.stage(event.id, handle) : watchApi.stage(event.id)
      request.then(
        (res) => {
          if (cancelled) return
          if (handle && !sameHandle(res.data.stage.host.handle, handle)) {
            setFailure({ eventId: event.id, key, kind: 'not_found' })
            return
          }
          shown.keys.add(key)
          remember(event.id, key, res.data)
          setFailure(null)
        },
        (error: unknown) => {
          if (cancelled) return
          failures += 1
          const refused = apiFailure(error)
          if (handle && refused.status === 404 && refused.type === 'stage_not_found') {
            setFailure({ eventId: event.id, key, kind: 'not_found' })
            return
          }
          const unavailable = refused.status === 404
          const reload = shown.keys.has(key) || onScreenRef.current
          if (!reload) setFailure({ eventId: event.id, key, kind: unavailable ? 'unavailable' : 'failed' })
          if (reload || unavailable) retry = setTimeout(load, retryDelayMs(failures))
        },
      )
    }
    setFailure(null)
    load()
    return () => {
      cancelled = true
      if (retry !== null) clearTimeout(retry)
    }
  }, [event.id, key, handle, token, authReady, attempt, remember])

  // The stage on screen: the one the URL names, once loaded (or primed by the switcher); while another loads, the one
  // shown last, so the room stays.
  const fetched = cached?.[key] ?? null
  const [held, setHeld] = useState<{ eventId: string; stage: StagePayload } | null>(null)
  if (fetched && (held?.stage !== fetched || held.eventId !== event.id)) setHeld({ eventId: event.id, stage: fetched })
  const onScreen = fetched ?? (held?.eventId === event.id ? held.stage : null)
  useLayoutEffect(() => {
    onScreenRef.current = onScreen !== null
  })
  // The viewer only while it speaks for the signed-in account: after signing out (or into another account) the old
  // one's can_host mustn't stay on screen while the reload runs.
  const userId = user?.id ?? null
  const stage = useMemo(
    () => (onScreen?.viewer && onScreen.viewer.user_id !== userId ? { ...onScreen, viewer: null } : onScreen),
    [onScreen, userId],
  )

  const failed = failure?.eventId === event.id && failure.key === key ? failure.kind : null
  const mainStagePath = stagePath({ shortcode, handle: null, search })
  // The event's logo top left in the site's header, in place of StreamBridge's, when the event asks (the payload's
  // `logo_in_header`, true only with a logo), going to its main stage as the stage switcher would: the leave guard
  // lets it through while live (`useSiteLinks`' `eventPath`).
  const headerLogo = event.logo_in_header === true && event.logo_url ? event.logo_url : null
  const eventLogo = useMemo(
    () => (headerLogo ? { src: headerLogo, name: event.name, to: mainStagePath } : null),
    [headerLogo, event.name, mainStagePath],
  )
  useWatchShellEventLogo(eventLogo)
  // A handle with no stage, after another stage was on screen: the main stage instead, saying why.
  const lost = failed === 'not_found' && stage !== null
  useEffect(() => {
    if (!lost) return
    toast({ title: 'Stage not found', description: 'You’re on the main stage.' })
    navigate(mainStagePath, { replace: true })
  }, [lost, mainStagePath, navigate])

  const showStage = useCallback(
    ({ handle: next, payload: primed, path, replace = false }: StageRequest) => {
      if (primed) remember(event.id, stageKey(next), primed, true)
      navigate(path, { replace })
    },
    [event.id, navigate, remember],
  )
  // Asks the server for the stage on screen again (what this account may do there changed: an invite came, they went on
  // or off the stage); the stage stays on screen meanwhile.
  const reloadStage = useCallback(() => setAttempt((n) => n + 1), [])

  if (!stage) {
    return (
      <WatchScreenFrame title={event.name} distanceKm={payload.route?.distance_km} routes={payload.routes} fill={!failed}>
        {failed === 'not_found' ? (
          <StageNotFound mainStagePath={mainStagePath} />
        ) : failed === 'unavailable' ? (
          <WatchScreenMessage>
            <Notice>{STAGE_UNAVAILABLE}</Notice>
          </WatchScreenMessage>
        ) : failed === 'failed' ? (
          <WatchScreenLoadFailed onRetry={() => setAttempt((n) => n + 1)} />
        ) : (
          <WatchScreenSkeleton />
        )}
      </WatchScreenFrame>
    )
  }

  return (
    <WatchRoom key={event.id} eventId={event.id}>
      {(status) => (
        <StagePulseProvider stageId={stage.stage.id} selfId={user?.id ?? null}>
          <WatchScreen
            payload={payload}
            stage={stage}
            status={status}
            shortcode={shortcode}
            onShowStage={showStage}
            onReloadStage={reloadStage}
            onStagesRemoved={forgetStages}
          />
        </StagePulseProvider>
      )}
    </WatchRoom>
  )
}

/** A handle with no stage on the event: say so, with the way to the event's main stage. */
function StageNotFound({ mainStagePath }: { mainStagePath: string }) {
  return (
    <WatchScreenMessage>
      <EmptyState
        icon={<UserRoundX />}
        title="Stage not found"
        description="Check the link you were sent."
        action={<ButtonLink to={mainStagePath}>Go to the main stage</ButtonLink>}
      />
    </WatchScreenMessage>
  )
}

/**
 * Inside the room. The chat, the host's presence and the intent completion live here, above any layout: a remount of
 * the presence while live would free the host's seat. The chat's state lives here too, so the header and the stage
 * are memoised with stable props: typing or a new message re-renders the chat column, not the canvas and the map.
 *
 * Stages (`useEventStages`): with more than one on the event, the desktop's bar holds the stage switcher, and a phone's
 * info line and chat head open it as a sheet (with one, the info line opens the event's details). Picking one shows it
 * (`onShowStage`): its faces, its host's call, its chat (`chat.<stage id>`) and its faces' voices, in the same room. A
 * host live on one stage stays live on it while they watch another (the presence keeps the stage it went live on, and
 * the dock stays for their microphone, camera and End); calling a camera and going live act on the stage on screen,
 * and only where they host it. A stage that goes away while on screen (its host removed) hands over to the main stage,
 * saying so.
 *
 * The layout follows the screen (the shell's, `useWatchShell`) without remounting anything that plays: the canvas keeps
 * its place, and the camera strip, the map and the chat render into nodes of their own (`useMovableNode`) that move
 * between the desktop's columns and the phone's panels. Their videos, the Mapbox instance and the chat's scroll and
 * focus stay. The layout and the phone's tab hold while the viewer types in the chat (`typing`): a keyboard squeezing
 * the screen, or a camera going live mid-message, mustn't move or hide the composer.
 */
interface WatchScreenProps {
  /** The viewer payload as the event's public channel keeps it (`useLiveViewerPayload`). */
  payload: ViewerPayload
  stage: StagePayload
  status: WatchRoomStatus
  /** The event's shortcode, for a stage's path when the API sent none. */
  shortcode: string
  /** Shows another stage of the event (WatchScreenPage's): its URL, the stage primed meanwhile. */
  onShowStage(request: StageRequest): void
  /** Loads the stage on screen again, for what this account may do there now (`viewer.invited`, say). */
  onReloadStage(): void
  /** Stages the event no longer has (`useEventStages().removed`), for the page to forget. */
  onStagesRemoved(ids: ReadonlySet<string>): void
}

function WatchScreen({ payload, stage, status, shortcode, onShowStage, onReloadStage, onStagesRemoved }: WatchScreenProps) {
  const { event } = payload
  const stageId = stage.stage.id
  const { user, authReady, token } = useAuth()
  const { search } = useLocation()
  const screen = useWatchScreen({ payload, stage, connection: status.connection })
  useWatchingStage(stage.stage, status.token, status.connection === 'connected')
  // Kept current by a timer at the event's start and end: an open screen turns from warming up to a quiet stretch, and
  // then to all finished, by itself.
  const phase = useEventPhase(event.starts_at, event.ends_at)
  // The pre-show (docs/features/pre-show-page.md), where its organiser turned it on: before a scheduled event has anything live, its viewers see the
  // countdown on the Ink globe and may register for the go-live email. Its hosts keep the watch screen to go live,
  // unless they open the page with ?preshow to see what their audience sees.
  const preShowAllowed = preShowEligible({
    enabled: event.pre_show === true,
    startsAt: event.starts_at,
    phase,
    canHost: stage.viewer?.can_host === true,
    forced: new URLSearchParams(search).has('preshow'),
  })
  const anythingLive =
    screen.live.members.length > 0 ||
    Object.values(screen.roomStages ?? {}).some((live) => live.members.length > 0) ||
    screen.cameras.some((camera) => camera.online)
  const [preShowStep, finishPreShow] = usePreShowStep({
    eligible: preShowAllowed,
    phase,
    live: anythingLive,
    connected: status.connection === 'connected',
  })
  const preShow = preShowStep === 'waiting' || preShowStep === 'entering'
  const registrations = useRegistrations(event.id, { enabled: preShow, token })
  const { layout, typing } = useWatchShell(preShow ? 'preshow' : 'room')
  const room = useRoomContext()
  // Full screen (FullscreenToggle, on the canvas): the page lays the canvas over the whole screen. Leaving the watch
  // screen leaves it, so the next page isn't left full screen.
  const { active: fullscreen } = useFullscreen()
  useEffect(() => leaveFullscreen, [])
  // The play world's sponsors, course points and hotspots: the payload's, then the room's newer ones as it announces them.
  const world = useWorldLayer({ eventId: event.id, initial: payload.world, version: screen.worldVersion })
  const { stages, removed } = useEventStages({ eventId: event.id, roomStages: screen.roomStages, current: stage.stage })
  const switchable = stages.length > 1
  // The Stages sheet, and what opened it: a phone's info line, stage button or (on its side) chat's head (`head`), or the bar's All
  // stages (the desktop, its stages overflowing). Opened on a phone, it closes once the layout is the desktop's: neither
  // opens anything there, and the bar lists the stages.
  const [stagesOpen, setStagesOpen] = useState<'head' | 'bar' | null>(null)
  if (stagesOpen && (!switchable || (stagesOpen === 'head' && layout === 'desktop'))) setStagesOpen(null)
  const requestFor = useCallback(
    (target: EventStage, replace = false): StageRequest => {
      const handle = target.kind === 'person' ? target.host.handle : null
      const info: StageInfo = target.info ?? {
        id: target.id,
        host: target.host,
        called_identity: null,
        members: [],
        ...(target.kind ? { kind: target.kind } : {}),
        owner_user_id: target.ownerUserId,
      }
      return { handle, payload: { stage: info, viewer: null }, path: stagePath({ path: target.path, shortcode, handle, search }), replace }
    },
    [shortcode, search],
  )
  const shownStage = useRef(stageId)
  useLayoutEffect(() => {
    shownStage.current = stageId
  })
  const selectStage = useCallback(
    (target: EventStage) => {
      setStagesOpen(null)
      if (target.id !== shownStage.current) onShowStage(requestFor(target))
    },
    [onShowStage, requestFor],
  )
  const openStages = useCallback(() => setStagesOpen('head'), [])
  const openAllStages = useCallback(() => setStagesOpen('bar'), [])
  // The event's details, from a phone's info line when the event has one stage.
  const [detailsOpen, setDetailsOpen] = useState(false)
  const openDetails = useCallback(() => setDetailsOpen(true), [])
  const onStagesOpenChange = useCallback((open: boolean) => {
    if (!open) setStagesOpen(null)
  }, [])
  // A name that draws nothing (the server keeps one of only invisible characters) is the handle, on every surface that
  // names the host: the chat's head, the phone's header, Back to host and the host marks' initials.
  const host = useMemo<PublicProfile>(
    () => ({
      ...stage.stage.host,
      name: nameOrHandle(stage.stage.host.name, stage.stage.host.handle, 'Host'),
      avatar_url: profileAvatarUrl(stage.stage.host.avatar_url),
    }),
    [stage.stage.host],
  )

  const hasMap = useHasMap(payload)
  // The map with nothing to frame for now (the stage hides it under its pin): the phone opens on Chat, as with no map.
  // Once something frames it, the map counts from then on, so a camera dropping doesn't swing the tab back.
  const unframed = !useLatched(!unframedMap(payload.route, screen.cameras))
  // A host has a seat on the stage: the phone opens on Chat. The chat's head (and the phone's header) ring the host's
  // avatar and say "live on stage" only while the room is connected and the host's participant is in it too: a seat
  // record can outlive the host's connection.
  const hasLiveHost = screen.live.members.some((member) => member.role === 'host')
  const hostInRoom = useInRoom(screen.live.members.filter((member) => member.role === 'host').map((member) => member.identity))
  const connected = status.connection === 'connected'
  const hostLive = connected && hostInRoom
  // A host's map as the stage's call (docs/features/follow-host-map.md): this tab sends its view while the map is its
  // own, and follows it while it is another host's.
  const hostMap = useHostMapView({ stageId, calledIdentity: screen.calledIdentity, members: screen.live.members, localIdentity: status.identity })
  const phoneTab = usePhoneTab({
    hasLiveHost,
    hasMap,
    unframed,
    mapCalled: hostMap.host !== null && !hostMap.sending,
    hold: typing,
  })
  // On its side, what fills the screen: the video, the camera grid or the chat beside the video, or the map. The viewer's
  // pick stays theirs, turned upright and back too.
  const [landscapeView, setLandscapeView] = useState<LandscapeView>('video')
  // Upright, the map filling the screen (the Map tab's full-screen toggle); only while the Map tab is open.
  const [fullMapAsked, setFullMap] = useState(false)
  const landscape = layout === 'landscape'
  // Turning the phone carries on where the viewer was: sideways from the full map, the map; upright from the cameras, the
  // chat or the map on its side, that tab (the map filling the screen); upright from the video, the screen as it was.
  // Sideways again, the view picked there comes back while upright stayed on its tab; another tab picked upright
  // meanwhile leaves it behind, and the video fills the screen.
  const [seenLayout, setSeenLayout] = useState(layout)
  if (seenLayout !== layout) {
    setSeenLayout(layout)
    if (layout === 'landscape' && fullMapAsked && phoneTab.tab === 'map') setLandscapeView('map')
    else if (seenLayout === 'phone' && layout === 'landscape' && landscapeView !== 'video' && landscapeView !== phoneTab.tab) setLandscapeView('video')
    if (seenLayout === 'landscape' && layout === 'phone') {
      if (landscapeView === 'cameras') phoneTab.pick('cameras')
      if (landscapeView === 'chat') phoneTab.pick('chat')
      if (landscapeView === 'map') phoneTab.pick('map')
      setFullMap(landscapeView === 'map' && hasMap)
    }
  }
  // Another tab (picked, or shown by the page: a sign-in to chat) puts the full map away; so does the desktop.
  if (fullMapAsked && (layout === 'desktop' || (layout === 'phone' && phoneTab.tab !== 'map'))) setFullMap(false)
  const fullMap = layout === 'phone' && phoneTab.tab === 'map' && hasMap && fullMapAsked
  useWatchShellImmersive(fullMap)
  // The map is on screen: the desktop's (in the canvas or its card), the phone's Map tab, or the map on its side.
  const mapShown = layout === 'desktop' || (layout === 'phone' && phoneTab.tab === 'map') || (landscape && landscapeView === 'map')
  // Upright on a phone the event's bar gives way to the info line under the video (the full map has neither).
  const phoneInfo = layout === 'phone' && !fullMap
  const chatBeside = landscape && landscapeView === 'chat'
  // The chat counts what arrives as unread while it is off screen: another phone tab, or another view on its side.
  const chatActive = layout === 'desktop' || (layout === 'phone' && phoneTab.tab === 'chat') || chatBeside
  // The video as a small player over a map filling the screen; tapped, back to the video.
  const pip = fullMap || (landscape && landscapeView === 'map')
  const layoutRef = useRef(layout)
  useLayoutEffect(() => {
    layoutRef.current = layout
  })
  const closePip = useCallback(() => {
    if (layoutRef.current === 'landscape') setLandscapeView('video')
    else setFullMap(false)
  }, [])
  // A camera picked in the strip: watch it; on its side (where the grid is the Cameras view) back to the video to see it.
  const { select: selectCamera } = screen
  const pickCamera = useCallback(
    (identity: string) => {
      selectCamera(identity)
      if (layoutRef.current === 'landscape') setLandscapeView('video')
    },
    [selectCamera],
  )
  const { showChat: showChatTab, pick: pickTab } = phoneTab
  // Typing in the chat on a phone picks its tab, so nothing the opening rule follows moves it away mid-message or after.
  const onComposerFocus = useCallback(() => {
    if (layoutRef.current === 'phone') pickTab('chat')
  }, [pickTab])
  // Something needs the chat on screen (a sign-in to chat, a chat intent completing): open its tab, or its view.
  const showChat = useCallback(() => {
    const current = layoutRef.current
    if (current === 'desktop') return
    showChatTab()
    if (current === 'landscape') setLandscapeView('chat')
  }, [showChatTab])

  // Sign-in at intent: the sheet names what the viewer was doing (nothing, from the site's Log in); the username step
  // runs what comes next once saved.
  const [signIn, setSignIn] = useState<{ open: boolean; kind: WatchIntent['kind'] | null }>({ open: false, kind: 'chat' })
  const [usernameStep, setUsernameStep] = useState<{ open: boolean; then: Pending }>({ open: false, then: { kind: 'chat' } })
  const openSignIn = useCallback((kind: WatchIntent['kind'] | null) => setSignIn({ open: true, kind }), [])
  const askUsername = useCallback((then: Pending) => setUsernameStep({ open: true, then }), [])
  // The pre-show's Remind me: registers someone signed in, and asks anyone else to sign in first (the intent registers
  // them when they're back).
  const { register: registerReminder } = registrations
  const remind = useCallback(async () => {
    if (!user) openSignIn('remind')
    else await registerReminder()
  }, [user, openSignIn, registerReminder])

  const onSignInNeeded = useCallback(
    (reason: SignInReason) => {
      showChat()
      if (reason === 'username_required') askUsername({ kind: 'chat' })
      else openSignIn('chat')
    },
    [askUsername, openSignIn, showChat],
  )
  // A guest's camera check (W3), open from their invite's Join stage. The ref answers the presence's username callback,
  // which comes in the middle of a join.
  const [guestCheck, setGuestCheckState] = useState(false)
  const guestCheckOpen = useRef(false)
  const setGuestCheck = useCallback((open: boolean) => {
    guestCheckOpen.current = open
    setGuestCheckState(open)
  }, [])
  // Going live without a username (a fallback: the page asks first): the step, then the check again. A guest's check
  // closes for it, since a sheet over it would close it anyway.
  const onUsernameNeeded = useCallback(() => {
    if (!guestCheckOpen.current) {
      askUsername({ kind: 'go_live' })
      return
    }
    setGuestCheck(false)
    askUsername({ kind: 'join_stage' })
  }, [askUsername, setGuestCheck])
  // The payload's viewer as fetched: a reload (after an Undo, or signing in) seeds the chat's block again.
  const chat = useStageChat(stageId, { active: chatActive, onSignInNeeded, viewer: stage.viewer })
  // The stage this tab goes live on: the one on screen, except while live (or going live), when it is the one it went
  // live on, whichever is on screen: watching another stage never ends the live.
  const [presenceStageId, setPresenceStageId] = useState(stageId)
  const { roomStages } = screen
  const presenceMembers = useMemo(
    () => (roomStages ? (roomStages[presenceStageId]?.members ?? []) : null),
    [roomStages, presenceStageId],
  )
  const presence = useStagePresence({
    stageId: presenceStageId,
    identity: status.identity,
    roomMembers: presenceMembers,
    onReconnectNeeded: status.refresh,
    onUsernameNeeded,
  })
  // Going live too: by then the seat is taken and LiveKit is publishing, and leaving would end the live it is starting.
  const onStage = presence.state === 'live' || presence.state === 'joining'
  // A live host's own sources (Studio mode, a screen), on the stage they're live on.
  const presenceCalled = roomStages?.[presenceStageId]?.calledIdentity ?? (presenceStageId === stageId ? screen.calledIdentity : null)
  const hostSources = useHostSources({
    stageId: presenceStageId,
    identity: status.identity,
    live: presence.state === 'live' && presence.role === 'host',
    members: presenceMembers,
    calledIdentity: presenceCalled,
  })
  if (!onStage && presenceStageId !== stageId) setPresenceStageId(stageId)
  // Live on another stage of the event than the one on screen: the dock says which (and goes back to it), and that
  // stage's faces stay audible, as the people this viewer is live with.
  const liveElsewhere = onStage && presenceStageId !== stageId
  const liveStage = liveElsewhere ? (stages.find((candidate) => candidate.id === presenceStageId) ?? null) : null
  const liveOn = useMemo(
    () => (liveElsewhere ? { name: liveStage && sentenceName(liveStage.host), onShow: liveStage ? () => selectStage(liveStage) : undefined } : null),
    [liveElsewhere, liveStage, selectStage],
  )
  // Their stage's Badger too, while he is in the room: he talks with them.
  const cohostElsewhere = useCohostIdentity(room, liveElsewhere ? presenceStageId : null)
  const heardElsewhere =
    liveElsewhere && presenceMembers ? [...presenceMembers.map((member) => member.identity), ...(cohostElsewhere ? [cohostElsewhere] : [])].join('\n') : ''
  // How loud the source under the faces plays (docs/features/stage-volume.md): the stage's level, and on a live host's
  // own tab the one their drag is setting.
  const sourceLevel = useSourceVolume(stageId, screen.sourceVolume)
  const audioIdentities = useMemo(
    () => (heardElsewhere ? Array.from(new Set([...screen.audioIdentities, ...heardElsewhere.split('\n')])) : screen.audioIdentities),
    [screen.audioIdentities, heardElsewhere],
  )
  useWarnBeforeUnload(onStage)
  // The site's header and footer ask before they leave (their `beforeLeave`): while live, the page asks first. Signed
  // out, the header's Log in opens the sheet here; Edit profile comes back to this screen, by the event's name.
  const onSignInFromSite = useCallback(() => openSignIn(null), [openSignIn])
  const siteLinks = useSiteLinks({ onStage, eventPath: stagePath({ shortcode, handle: null }) })
  const { beforeLeave } = siteLinks
  useWatchShellSite({ beforeLeave, onLogIn: onSignInFromSite, profileLabel: event.name })
  const [leaveFailure, setLeaveFailure] = useState(() => endFailureMessage(null))

  const latest = useRef({ chat, presence, user })
  useLayoutEffect(() => {
    latest.current = { chat, presence, user }
  })
  // A camera check open on the last stage closes: going live from it would land on the stage now on screen.
  const checkedFor = useRef(stageId)
  useEffect(() => {
    if (checkedFor.current === stageId) return
    checkedFor.current = stageId
    if (guestCheckOpen.current) setGuestCheck(false)
    if (latest.current.presence.state === 'checking') latest.current.presence.cancel()
  }, [stageId, setGuestCheck])

  // Removed stages leave the page's cache: a host invited again gets a new stage under the same handle.
  useEffect(() => {
    if (removed.size > 0) onStagesRemoved(removed)
  }, [removed, onStagesRemoved])
  // The stage on screen went away (the organiser removed its host): the main stage instead, saying why.
  const gone = removed.has(stageId)
  const goneRef = useRef({ host, stages, requestFor, onShowStage, shortcode, search })
  useLayoutEffect(() => {
    goneRef.current = { host, stages, requestFor, onShowStage, shortcode, search }
  })
  useEffect(() => {
    if (!gone) return
    const current = goneRef.current
    toast({ title: `${current.host.name}’s stage ended`, description: 'You’re on the main stage.' })
    const main = current.stages.find((candidate) => candidate.kind !== 'person')
    current.onShowStage(
      main
        ? current.requestFor(main, true)
        : { handle: null, path: stagePath({ shortcode: current.shortcode, handle: null, search: current.search }), replace: true },
    )
  }, [gone])
  // ── A guest's invitation (W3) ──
  // The viewer's invite on the stage on screen is open while the server says an invited seat waits for them
  // (`viewer.invited`), while the room seats them here as a guest on another connection (a dropped tab: Join moves the
  // seat here), or for an invite card that arrived since the stage loaded (until the server's word on it comes back).
  // Not for the stage's hosts, nor while this tab is on it as a guest, nor once answered here (declined, or lapsed).
  const viewerId = user?.id ?? null
  const { invite: inviteCard, about: aboutMeId } = useMemo(() => cardsAbout(chat.messages, viewerId), [chat.messages, viewerId])
  const inviteCardId = inviteCard?.id ?? null
  const seatedHere = viewerId !== null && screen.live.members.some((member) => member.userId === viewerId)
  // On another connection: this tab's own seat, lowered (a host took the guest off), lingers in the metadata a moment.
  const seatedHereAsGuest =
    viewerId !== null &&
    screen.live.members.some((member) => member.userId === viewerId && member.role === 'guest' && member.identity !== status.identity)
  const guestOnStage = onStage && presence.role === 'guest'
  const guestHere = guestOnStage && presenceStageId === stageId
  const [fresh, setFresh] = useState<{ viewer: StagePayload['viewer']; cardId: string } | null>(null)
  const freshInvite = fresh !== null && fresh.viewer === stage.viewer && fresh.cardId === inviteCardId
  // Answers given in this tab, by invite: the card's id, or the pinned prompt's stage.
  const [answers, setAnswers] = useState<Record<string, StageCardAnswer>>({})
  const inviteKey = inviteCardId ?? `stage:${stageId}`
  const localAnswer = answers[inviteKey]
  const inviteOpen =
    viewerId !== null &&
    !screen.isHost &&
    !guestHere &&
    localAnswer === undefined &&
    (stage.viewer?.invited === true || seatedHereAsGuest || freshInvite)
  const inviteAnswer: StageCardAnswer = guestHere ? (presence.state === 'live' ? 'joined' : 'waiting') : (localAnswer ?? !inviteOpen)
  const hostName = sentenceName(stage.stage.host)
  // Open only because another tab of theirs is on this stage as a guest (no invitation waits, and Join moves the seat
  // here): Not now would change nothing, so the invite says where they are instead.
  const inviteElsewhere = inviteOpen && seatedHereAsGuest && stage.viewer?.invited !== true && !freshInvite
  const inviteNote = inviteElsewhere ? 'On stage in another tab' : undefined
  const inviteRef = useRef({ key: inviteKey, hostName })
  // The stage this tab joins and is on as a guest (the presence's, `presenceStageId`): the invite it joined from and its
  // host, so the welcome, a lapsed invitation and Leave stage act on it, whichever stage is on screen by then. Taken as
  // Join stage is pressed (the presence goes live on the stage on screen), and while that stage is on screen.
  const guestJoin = useRef<{ key: string; hostName: string } | null>(null)
  useLayoutEffect(() => {
    inviteRef.current = { key: inviteKey, hostName }
    if (guestHere) guestJoin.current = { key: inviteKey, hostName }
  })

  // What says the invitation changed: a card about the viewer (an invite, their joined or left), or the room seating or
  // unseating them here. The stage loads again for the server's word; a new invite card is open meanwhile (before
  // paint, so it never shows answered first). Measured from the stage's first history, so cards already in it change
  // nothing, with one exception: an invite sent while the stage loaded is in that history but not in the stage fetched
  // before it, so a first history whose latest card about the viewer is an invite the server's word doesn't hold loads
  // the stage once more.
  const facts = useRef<{ stageId: string; about: string | null; invite: string | null; seated: boolean } | null>(null)
  useLayoutEffect(() => {
    if (viewerId === null || chat.loading) return
    const before = facts.current
    facts.current = { stageId, about: aboutMeId, invite: inviteCardId, seated: seatedHere }
    if (!before || before.stageId !== stageId) {
      if (inviteCardId !== null && inviteCardId === aboutMeId && stage.viewer?.invited !== true) onReloadStage()
      return
    }
    if (inviteCardId && inviteCardId !== before.invite) setFresh({ viewer: stage.viewer, cardId: inviteCardId })
    if (aboutMeId !== before.about || seatedHere !== before.seated) onReloadStage()
  }, [viewerId, chat.loading, stageId, aboutMeId, inviteCardId, seatedHere, stage.viewer, onReloadStage])

  // An open invite brings the chat on screen once (the phone's Chat tab, the chat beside the video on its side): on load, for a
  // link accepted elsewhere, and when a host's invite arrives.
  const shownInvite = useRef<string | null>(null)
  useEffect(() => {
    if (!inviteOpen || shownInvite.current === inviteKey) return
    shownInvite.current = inviteKey
    showChat()
  }, [inviteOpen, inviteKey, showChat])

  const openGuestCheck = useCallback(() => {
    const { state, role } = latest.current.presence
    if (state === 'live' || state === 'joining') {
      toast({ tone: 'error', title: 'You’re on another stage', description: role === 'guest' ? 'Leave it first.' : 'End that live first.' })
      return
    }
    setGuestCheck(true)
  }, [setGuestCheck])

  // Join stage, on the card or the pinned prompt: the username first if the account has none, then the camera check.
  const joinStage = useCallback(() => {
    const current = latest.current.user
    if (!current) return
    if (!current.attributes.username?.trim()) askUsername({ kind: 'join_stage' })
    else openGuestCheck()
  }, [askUsername, openGuestCheck])

  // The check's Join stage: the presence opens the devices it picked and goes live as a guest. On stage, the check
  // closes with a word. An invitation no longer open (403: a host withdrew it, or it lapsed) closes it too, quiets the card
  // and reloads the stage (the viewer payload); a username step takes over from it
  // (`onUsernameNeeded`); a cancel needs nothing. Anything else stays in the check, in the presence's words.
  const joinWith = useCallback(
    async (choice: CameraCheckChoice) => {
      const joining = { ...inviteRef.current }
      guestJoin.current = joining
      try {
        await latest.current.presence.join(choice)
      } catch (failure) {
        if (!(failure instanceof StagePresenceFailure)) throw failure
        if (failure.code === 'cancelled' || failure.code === 'username_required') return
        if (failure.code !== 'forbidden') throw failure
        setGuestCheck(false)
        setAnswers((current) => ({ ...current, [joining.key]: true }))
        toast({ tone: 'error', title: 'Your invitation is no longer open', description: 'Ask a host to invite you again.' })
        onReloadStage()
        return
      }
      setGuestCheck(false)
      toast({ title: `You’re on ${joining.hostName}’s stage` })
    },
    [setGuestCheck, onReloadStage],
  )
  const cancelGuestCheck = useCallback(() => {
    setGuestCheck(false)
    // A join under way stops, and a seat the API took goes back (nothing while idle or live).
    latest.current.presence.cancel()
  }, [setGuestCheck])

  // Not now: the invitation lapses, quietly (no card). The card reads Declined; the stage loads again.
  const declineInvite = useCallback(async () => {
    const { key } = inviteRef.current
    await invitesApi.declineInvite(stageId)
    setAnswers((current) => ({ ...current, [key]: 'declined' }))
    onReloadStage()
  }, [stageId, onReloadStage])

  const onDecline = inviteElsewhere ? undefined : declineInvite
  const chatInvite = useMemo<ChatInvite | null>(
    () => (inviteCard ? { cardId: inviteCard.id, answered: inviteAnswer, onJoin: joinStage, onDecline, note: inviteNote } : null),
    [inviteCard, inviteAnswer, joinStage, onDecline, inviteNote],
  )
  // No card in the chat carries an open invite (a stage link accepted elsewhere, or the card paged out of history): the
  // same card, pinned above the composer, from the stage's host to the viewer.
  const stageHost = stage.stage.host
  const promptCard = useMemo(() => {
    if (!user) return null
    const { attributes } = user
    return {
      kind: 'invite' as const,
      user: { id: stage.stage.owner_user_id ?? '', handle: stageHost.handle, name: stageHost.name, avatar_url: stageHost.avatar_url },
      subject: { id: user.id, handle: attributes.username ?? null, name: attributes.first_name ?? null, avatar_url: attributes.avatar_url ?? null },
      created_at: '',
    }
  }, [user, stageHost, stage.stage.owner_user_id])
  const invitePrompt =
    !inviteCard && inviteOpen && promptCard ? (
      <StageCard
        as="div"
        card={promptCard}
        viewerId={viewerId}
        answered={inviteAnswer}
        onJoin={joinStage}
        onDecline={onDecline}
        note={inviteNote}
      />
    ) : null

  // A live that ends some other way than Leave stage or End (a host took a guest off, the connection dropped) says why
  // where no dock is left to say it: a guest's dock goes with their live, and so does a host's while they watch another
  // stage they don't host. Leave stage and End say so themselves; a dock that stays shows the presence's words.
  const unseenDrop = useRef(false)
  useEffect(() => {
    const { state, role, error } = presence
    if (unseenDrop.current && state === 'idle' && error) {
      toast(QUIET_DROPS.has(error.code) ? { title: error.message } : { tone: 'error', title: error.message })
    }
    unseenDrop.current = state === 'live' && (role === 'guest' || (presenceStageId !== stageId && !screen.isHost))
  }, [presence, presenceStageId, stageId, screen.isHost])
  // Left: the invite that brought them on is used up (the server's word follows with the left card), so it doesn't
  // offer Join stage again meanwhile; their stage's, not the one on screen's.
  const onGuestLeft = useCallback(() => {
    const joined = guestJoin.current
    if (joined) setAnswers((current) => ({ ...current, [joined.key]: true }))
    toast({ title: 'You left the stage' })
  }, [])
  // Once on stage, focus goes from the check to the guest's microphone; a check closed without joining returns it to
  // the Join stage it came from.
  const guestDockFocus = useRef<HTMLButtonElement>(null)
  const guestCheckFinalFocus = useCallback(() => guestDockFocus.current ?? true, [])

  const run = useCallback(
    (action: Pending) => {
      const current = latest.current
      if (action.kind === 'chat') {
        showChat()
        void (action.draft === undefined ? current.chat.send() : current.chat.send(action.draft))
      } else if (action.kind === 'join_stage') openGuestCheck()
      else if (current.presence.state === 'idle') void current.presence.check()
    },
    [showChat, openGuestCheck],
  )

  // Signed out: sign in first. No username: pick one first (a sheet opened over the check would close it).
  const goLive = useCallback(() => {
    const current = latest.current.user
    if (!current) openSignIn('go_live')
    else if (!current.attributes.username?.trim()) askUsername({ kind: 'go_live' })
    else run({ kind: 'go_live' })
  }, [askUsername, openSignIn, run])

  // The username step's next action runs once the session has the username: the chat's send (and its pending row)
  // reads the user of the render it came from, and the step's save updates the session just before it calls back.
  const pending = usernameStep.then
  const [queued, setQueued] = useState<Pending | null>(null)
  const onUsernameDone = useCallback(() => {
    setUsernameStep((step) => ({ ...step, open: false }))
    setQueued(pending)
  }, [pending])
  const hasUsername = Boolean(user?.attributes.username?.trim())
  useEffect(() => {
    if (!queued || !hasUsername) return
    setQueued(null)
    run(queued)
  }, [queued, hasUsername, run])

  // What the viewer was doing before signing in completes once, when the session and the stage both speak for this
  // account: a chat intent sends its text, a go live opens the camera check (never goes live by itself).
  const takenFor = useRef<string | null>(null)
  useEffect(() => {
    const viewer = stage.viewer
    if (!authReady || !user || viewer?.user_id !== user.id || takenFor.current === user.id) return
    takenFor.current = user.id
    const saved = peekIntent(event.id)
    takeIntent(event.id)
    if (!saved || saved.intent.stageId !== stageId) return
    const { intent } = saved
    if (intent.kind === 'remind') {
      registerReminder().catch(() => toast({ title: 'We couldn’t set your reminder', description: 'Try Remind me again.', tone: 'error' }))
      return
    }
    if (intent.kind === 'go_live' && !viewer.can_host) return
    // Back in the tab that left with it, the draft is this tab's: sent meanwhile, or changed, it isn't the intent's
    // any more. (Another tab, opened by an emailed link, has no draft of its own.)
    if (intent.kind === 'chat' && saved.leftFromHere && chatBody(latest.current.chat.draft) !== chatBody(intent.draft)) return
    const action: Pending = intent.kind === 'chat' ? { kind: 'chat', draft: intent.draft } : { kind: 'go_live' }
    if (!user.attributes.username?.trim()) askUsername(action)
    else run(action)
  }, [authReady, user, stage.viewer, event.id, stageId, askUsername, run, registerReminder])

  const hostUserIds = useMemo(
    () => screen.live.members.filter((member) => member.role === 'host').map((member) => member.userId),
    [screen.live.members],
  )
  // The host's invites (W2), on the stage on screen and only where its payload says this viewer may invite there: an
  // API pod from before stage invites says nothing (no `can_invite`), and has none of their endpoints. Inviting is the
  // + face, the dock's Invite to stage and chat's Invite on stage; removing a guest is their face's menu, for its hosts.
  const canInvite = stage.viewer?.can_invite === true
  const inviteStageId = canInvite ? stageId : null
  const hostStageId = screen.isHost && stage.viewer?.can_invite !== undefined ? stageId : null
  const inviteFromChat = useCallback((person: ChatPerson) => inviteOnStage(stageId, person), [stageId])
  // Everyone seated on any of the event's stages (the room metadata's, this stage's before it): chat offers them no
  // Invite on stage, which the server would refuse as `already_on_stage`.
  const seatedUserIds = useMemo(() => {
    const everyStage = Object.values(screen.roomStages ?? {}).flatMap((live) => live.members)
    return [...new Set([...screen.live.members, ...everyStage].map((member) => member.userId))]
  }, [screen.roomStages, screen.live.members])
  const onSignInToChat = useCallback(() => openSignIn('chat'), [openSignIn])
  const intent: WatchIntent | null =
    signIn.kind === 'chat'
      ? { kind: 'chat', eventId: event.id, stageId, draft: chat.draft }
      : signIn.kind === 'go_live'
        ? { kind: 'go_live', eventId: event.id, stageId }
        : signIn.kind === 'remind'
          ? { kind: 'remind', eventId: event.id, stageId }
          : null
  // Every camera and Live show the event has, offline ones included: `live_only` hides those from the strip, but the
  // canvas still says they're offline rather than that the event has none.
  const cameraCount = useMemo(() => cameraCountOf(payload.cameras), [payload.cameras])
  // A Live show to wait for as the desktop opens (the payload lists it offline too, `live_only` or not).
  const hasLiveShow = payload.cameras.some((camera) => camera.source_role === 'programme')
  const hasStrip = useHasStrip(cameraCount, screen.cameras.filter((camera) => isHostSource(camera)).length)
  const roomTrouble = roomTroubleFrom(status)
  // After a refusal nothing tries again: the canvas stops spinning, and the notice says why and offers Try again.
  const canvasConnection: CanvasConnection = roomTrouble === 'unavailable' ? 'stopped' : status.connection

  // The parts that move between layouts, each in a node of its own; a slot places it.
  const watchHeadingId = useId()
  const stripNode = useMovableNode()
  const mapNode = useMovableNode('watch-fill watch-map')
  const chatNode = useMovableNode('watch-fill')
  const slots = useMemo(
    () => ({
      strip: <MovableSlot node={stripNode} className="watch-phone__cameras" />,
      map: <MovableSlot node={mapNode} className="watch-fill" />,
      chat: <MovableSlot node={chatNode} className="watch-fill" />,
    }),
    [stripNode, mapNode, chatNode],
  )
  // The dock: a host's (they host the stage on screen, or are live on another stage of the event, so their microphone,
  // camera and End stay at hand while they watch this one) or a guest's while on a stage. It renders into a node of its
  // own, placed under the canvas (the desktop, on its side, over the full map) or on the phone's info line, so a layout
  // change moves it without a remount: End's question and focus stay.
  const full = stageFull(screen.live.members, user?.id)
  const hostDocked = !guestOnStage && (screen.isHost || liveElsewhere)
  const guestDocked = guestOnStage && presence.state === 'live'
  const docked = hostDocked || guestDocked
  const seatsTaken = screen.live.members.length
  const dockInvite = useMemo(() => (inviteStageId ? { stageId: inviteStageId, seatsTaken } : null), [inviteStageId, seatsTaken])
  const dockNode = useMovableNode()
  const dockSlot = useMemo(() => <MovableSlot node={dockNode} className="watch-dock-slot" />, [dockNode])
  // Show my audience calls what the host sees: a camera they preview, else their own map while it is the live map on
  // their screen (live on this stage, and not the call already).
  const hostLiveHere = presence.state === 'live' && presence.role === 'host' && presenceStageId === stageId
  // Live on the stage on screen while its audience follows a camera or the Live show: the level for it under their voice.
  const audienceSource = hostLiveHere ? screen.audienceSource : null
  const audienceSound = useSourceAudio(audienceSource?.identity ?? null)
  const dockSourceVolume = useMemo(
    () =>
      audienceSource
        ? {
            source: audienceSource,
            sound: audienceSound.sound,
            track: audienceSound.track,
            level: sourceLevel.level,
            onPreview: sourceLevel.preview,
            onCommit: sourceLevel.commit,
            error: sourceLevel.error,
          }
        : null,
    [audienceSource, audienceSound, sourceLevel],
  )
  const dockPreview =
    screen.previewIdentity ?? (hostLiveHere && hostMap.shareable && !hostMap.sending && status.identity ? mapSource(status.identity) : null)

  // ── Broadcast elements (docs/features/badger-cohost.md) ──
  // The stage's graphics, drawn over the canvas for everyone watching it, the host included, only while a host is live
  // on it. The roster names and pictures athletes, so it is kept while something is on screen (and for a live host's
  // console) even with the map out of sight; positions stay polled only while the map shows.
  const races = useMemo(() => eventRaces(payload.routes?.length ? payload.routes : payload.route ? [payload.route] : []), [payload.routes, payload.route])
  const graphicsOnAir = useStageGraphics(stageId, screen.live.graphics)
  const graphics = hostLive ? graphicsOnAir : NO_VISIBLE_GRAPHICS
  const athletes = useEventAthletes(event.id, hasMap && mapShown, hostLiveHere || graphics !== NO_VISIBLE_GRAPHICS)
  // The host console's preview (hover or focus), on this host's canvas only.
  const [graphicsPreview, setGraphicsPreview] = useState<GraphicPreview | null>(null)
  const shownPreview = hostLiveHere ? graphicsPreview : null
  const roster = useMemo(() => rosterById(athletes.roster), [athletes.roster])
  const broadcastContext = useMemo<BroadcastContext>(
    () => ({ eventId: event.id, races, roster, compact: layout === 'phone', maxRows: layout === 'landscape' ? LANDSCAPE_BOARD_ROWS : null }),
    [event.id, races, roster, layout],
  )
  const broadcast = useMemo(
    () => (hostLive || shownPreview ? <BroadcastLayer graphics={graphics} preview={shownPreview} context={broadcastContext} /> : null),
    [hostLive, graphics, shownPreview, broadcastContext],
  )
  // Show on map: the host's world on their canvas (the desktop's canvas, the phone's Map tab or the map on its side),
  // flying to the athlete with their card open.
  const [athleteFocus, setAthleteFocus] = useState<{ id: string; key: number } | null>(null)
  const focusAthleteOnMap = useCallback(
    (athleteId: string) => {
      setAthleteFocus((last) => ({ id: athleteId, key: (last?.key ?? 0) + 1 }))
      if (layoutRef.current === 'phone') pickTab('map')
      else if (layoutRef.current === 'landscape') setLandscapeView('map')
    },
    [pickTab],
  )
  // Viewers follow a host's map on either basemap (docs/features/follow-host-map.md), so any course map will do.
  const mapUnavailable = hasMap ? null : 'This event has no course map'
  // The stage's one set of actions: the console's buttons, and Badger over LiveKit RPC.
  const stageActions = useStageActions({
    eventId: event.id,
    stageId,
    live: hostLiveHere,
    roster: athletes.roster,
    races,
    mapUnavailable,
    hostIdentity: status.identity,
    callCamera: screen.callCamera,
    focusAthleteOnMap,
  })
  // Badger (docs/features/badger-cohost.md, Phase 2): asked for (the stage's metadata) and in the room, for the console.
  const badger = useCohostConsole({ room, stageId, live: hostLiveHere, identity: status.identity, asked: screen.live.cohost })
  // Phase 3: while this host is live here, the stage's Badger runs those actions through their page, and their console
  // says what he did.
  const [badgerDid, setBadgerDid] = useState<CohostDone | null>(null)
  useCohostHandlers({ room, stageId, live: hostLiveHere, actions: stageActions, onDone: setBadgerDid })
  const graphicsControl = useMemo(
    () =>
      hostLiveHere ? (
        <GraphicsConsoleOpener
          roster={athletes.roster}
          races={races}
          onScreen={graphicsOnAir}
          actions={stageActions}
          mapUnavailable={mapUnavailable}
          onPreview={setGraphicsPreview}
          badger={badger}
          badgerDid={badgerDid}
        />
      ) : null,
    [hostLiveHere, athletes.roster, races, graphicsOnAir, stageActions, mapUnavailable, badger, badgerDid],
  )

  const dock = useMemo(
    () =>
      hostDocked ? (
        <WatchHostDock
          presence={presence}
          previewIdentity={dockPreview}
          onCallCamera={screen.callCamera}
          onGoLive={goLive}
          stageFull={full}
          invite={dockInvite}
          liveOn={liveOn}
          sources={hostSources}
          graphics={graphicsControl}
          sourceVolume={dockSourceVolume}
        />
      ) : guestDocked ? (
        <GuestDock presence={presence} onLeft={onGuestLeft} focusRef={guestDockFocus} liveOn={liveOn} />
      ) : null,
    [hostDocked, guestDocked, presence, dockPreview, screen.callCamera, goLive, full, dockInvite, liveOn, hostSources, graphicsControl, dockSourceVolume, onGuestLeft],
  )
  // A phone's info line: the meta under the event's name, and what the line opens.
  const live = connected && screen.cameras.some((camera) => camera.online)
  const length = courseLengthOf(payload.routes, payload.route?.distance_km)
  const infoMeta = useMemo(
    () => <WatchMetaWithAudience live={live} length={length} onStage={onStage} />,
    [live, length, onStage],
  )
  const info = phoneInfo ? (
    <WatchInfoLine
      title={event.name}
      host={host}
      hostLive={hostLive}
      members={screen.live.members}
      connected={connected}
      switchable={switchable}
      meta={infoMeta}
      open={switchable ? stagesOpen !== null : detailsOpen}
      onOpen={switchable ? openStages : openDetails}
    >
      {docked ? dockSlot : null}
    </WatchInfoLine>
  ) : null
  const detailsCourses = payload.routes?.length ? payload.routes : payload.route ? [payload.route] : []

  // The top of the chat, in the panel and in its failure's place alike. On the desktop and beside the video on a phone's
  // side, the chat's head: the host's only place there; on its side, with other stages on the event, it opens the stage
  // switcher. Upright on a phone the info line just above the chat names the host and opens the stages already, so the
  // chat has no head (founder: no wasted space at the top) and how many are on the stage shows in the faces. Then who's
  // online (the audience's one count in the chat), from the room once it connects; a failure there leaves the chat as
  // it is.
  const phoneSwitch = switchable && layout !== 'desktop'
  const chatHead = (
    <>
      {layout !== 'phone' && (
        <WatchChatHead
          host={host}
          hostLive={hostLive}
          members={screen.live.members}
          connected={connected}
          compact={layout === 'landscape'}
          onOpenStages={phoneSwitch ? openStages : undefined}
          stagesOpen={phoneSwitch && stagesOpen !== null}
        />
      )}
      {/* On the desktop the audience sits under the video instead (WatchStage), so the count shows once. */}
      {layout !== 'desktop' && (
        <WatchBoundary name="audience" fallback={null} resetKey={stageId}>
          <WatchAudience members={screen.live.members} placement="chat" />
        </WatchBoundary>
      )}
    </>
  )
  // On a phone on its side, with other stages on the event, the stage on screen as a button over the video that opens
  // the Stages sheet (upright, the info line opens it); beside the video the chat's head opens the same sheet.
  const stageButton = useMemo(
    () =>
      phoneSwitch ? (
        <WatchStageButton
          host={host}
          hostLive={hostLive}
          members={screen.live.members}
          connected={connected}
          open={stagesOpen !== null}
          onOpen={openStages}
        />
      ) : null,
    [phoneSwitch, host, hostLive, screen.live.members, connected, stagesOpen, openStages],
  )
  // The desktop's switcher, in the event's bar; memoised, so the bar's renders pass by while nothing in it changes.
  const barSwitcher = useMemo(
    () =>
      switchable && layout === 'desktop' ? (
        <StageSwitcher
          variant="bar"
          stages={stages}
          selectedId={stageId}
          onSelect={selectStage}
          connected={connected}
          onMore={openAllStages}
          moreOpen={stagesOpen !== null}
        />
      ) : null,
    [switchable, layout, stages, stageId, selectStage, connected, openAllStages, stagesOpen],
  )
  // On a phone the site's footer ends the Cameras panel's scroll (the screen itself doesn't scroll there).
  const phoneFooter = useMemo(
    () => (
      <div className="watch-phone__footer">
        <SiteFoot beforeLeave={beforeLeave} />
      </div>
    ),
    [beforeLeave],
  )

  // Ending the live first, then where the viewer asked to go (or signing out); a refusal keeps them here, saying so.
  // The live ending is enough by itself (`useSiteLinks` carries out what it held then); `proceed` runs it only once.
  const { held, stay, proceed } = siteLinks
  const endLiveAndLeave = useCallback(async () => {
    const live = latest.current.presence
    try {
      await live.leave()
    } catch (failure) {
      setLeaveFailure(live.role === 'guest' ? leaveFailureMessage(failure) : endFailureMessage(failure))
      throw failure
    }
    proceed()
  }, [proceed])
  // A guest leaves the stage rather than ending a live of their own.
  const guard = guestOnStage
    ? {
        title: 'Leave the stage?',
        description: held?.kind === 'sign_out' ? 'Signing out takes you off the stage.' : 'Leaving takes you off the stage.',
        confirm: held?.kind === 'sign_out' ? 'Leave stage and sign out' : 'Leave stage and go',
      }
    : {
        title: 'End your live?',
        description: held?.kind === 'sign_out' ? 'Signing out ends your live.' : 'Leaving ends your live.',
        confirm: held?.kind === 'sign_out' ? 'End live and sign out' : 'End live and leave',
      }

  if (preShow) {
    return (
      <>
        <Suspense fallback={<main className="pre-show" />}>
          <PreShow
            event={event}
            route={payload.route}
            summary={registrations.summary}
            entering={preShowStep === 'entering'}
            onRemind={remind}
            onUnregister={registrations.unregister}
            onEntered={finishPreShow}
          />
        </Suspense>
        <SignInSheet open={signIn.open} onOpenChange={(open) => setSignIn((current) => ({ ...current, open }))} intent={intent} />
      </>
    )
  }

  return (
    <main
      className="watch-screen"
      data-layout={layout}
      data-view={landscape ? landscapeView : undefined}
      data-full-map={fullMap || undefined}
      data-fullscreen={fullscreen || undefined}
    >
      {!phoneInfo && (
        <WatchEventBar
          title={event.name}
          bare={fullMap}
          live={live}
          audience
          distanceKm={payload.route?.distance_km}
          routes={payload.routes}
          layout={layout}
          onStage={onStage}
          stages={barSwitcher}
        />
      )}
      <div className="watch-screen__body">
        {/* First, so the page's h1 leads the outline and the Watch region; watch.css shows it under the video. */}
        {info}
        <section className="watch-screen__watch" aria-labelledby={watchHeadingId}>
          {/* The region's name and the h2 its empty states (h3) nest under. */}
          <h2 id={watchHeadingId} className="sr-only">
            Watch
          </h2>
          <RoomTrouble trouble={roomTrouble} onRetry={status.refresh} />
          <WatchStage
            eventId={event.id}
            route={payload.route}
            routes={payload.routes}
            worldElements={world.elements}
            hasMap={hasMap}
            hasStrip={hasStrip}
            cameraCount={cameraCount}
            hasLiveShow={hasLiveShow}
            phase={phase}
            connection={canvasConnection}
            screen={screen}
            hostMap={hostMap}
            host={host}
            localIdentity={status.identity}
            docked={docked}
            dockSlot={docked && !phoneInfo ? dockSlot : null}
            inviteStageId={inviteStageId}
            hostStageId={hostStageId}
            layout={layout}
            mapShown={mapShown}
            athletes={athletes}
            athleteFocus={athleteFocus}
            broadcast={broadcast}
            pip={pip}
            onPipOpen={closePip}
            onPickCamera={pickCamera}
            stripNode={stripNode}
            mapNode={mapNode}
          />
        </section>
        {layout === 'desktop' ? (
          <div className="watch-screen__chat">{slots.chat}</div>
        ) : (
          <PhoneTabs
            tab={phoneTab.tab}
            onTabChange={phoneTab.pick}
            unread={chat.unread}
            cameras={slots.strip}
            footer={phoneFooter}
            map={slots.map}
            chat={slots.chat}
            landscape={landscape}
            view={landscapeView}
            onViewChange={setLandscapeView}
            fullMap={fullMap}
            onFullMapChange={hasMap ? setFullMap : undefined}
            stageButton={stageButton}
          />
        )}
      </div>
      {createPortal(
        <WatchBoundary name="chat" fallback={(retry) => <ChatUnavailable onRetry={retry} head={chatHead} />} resetKey={stageId}>
          <ChatPanel
            chat={chat}
            signedIn={Boolean(user)}
            isHost={screen.isHost}
            hostUserIds={hostUserIds}
            currentUserId={user?.id ?? null}
            onSignIn={onSignInToChat}
            onComposerFocus={onComposerFocus}
            head={chatHead}
            onInviteOnStage={canInvite ? inviteFromChat : undefined}
            seatedUserIds={seatedUserIds}
            invite={chatInvite}
            prompt={invitePrompt}
            gifs={Boolean(stage.chat?.gifs)}
          />
        </WatchBoundary>,
        chatNode,
      )}
      {/* After the body, so its slot holds the node before the dock's own layout effects run. */}
      {createPortal(dock, dockNode)}

      <WatchAudioGate activeIdentity={screen.watchedIdentity} extraIdentities={audioIdentities} />
      <WatchAudio sourceIdentity={screen.sourceIdentity} sourceLevel={sourceLevel.level} />
      <WatchSheet open={stagesOpen !== null} onOpenChange={onStagesOpenChange} title="Stages" className="watch-stages-sheet">
        <StageSwitcher variant="list" stages={stages} selectedId={stageId} onSelect={selectStage} connected={connected} />
      </WatchSheet>
      <EventDetailsSheet open={detailsOpen && !switchable} onOpenChange={setDetailsOpen} event={event} courses={detailsCourses} />
      <SignInSheet open={signIn.open} onOpenChange={(open) => setSignIn((current) => ({ ...current, open }))} intent={intent} />
      <UsernameStep
        open={usernameStep.open}
        onOpenChange={(open) => setUsernameStep((step) => ({ ...step, open }))}
        onDone={onUsernameDone}
        intent={pending.kind === 'join_stage' ? 'go_live' : pending.kind}
      />
      <WatchGuestCheck
        open={guestCheck}
        hostName={host.name}
        onJoin={joinWith}
        onCancel={cancelGuestCheck}
        finalFocus={guestCheckFinalFocus}
      />
      <ConfirmDialog
        open={held !== null}
        onOpenChange={(open) => {
          if (!open) stay()
        }}
        title={guard.title}
        description={`${guard.description} Your audience stops seeing and hearing you.`}
        confirmLabel={guard.confirm}
        cancelLabel="Stay"
        onConfirm={endLiveAndLeave}
        failureMessage={leaveFailure}
      />
    </main>
  )
}


const WatchAudioGate = memo(LayoutAudioGate)

/**
 * The room's audio once the browser allows it (docs/features/stage-volume.md): every face at the viewer's own volume
 * (`useViewerVolume`, the speaker's), and the source under them (`sourceIdentity`) at the viewer's volume times the
 * stage's level (`sourceLevel`, 0–100). Muted, everything is 0.
 */
const WatchAudio = memo(function WatchAudio({ sourceIdentity, sourceLevel }: { sourceIdentity: string | null; sourceLevel: number }) {
  const { level, muted } = useViewerVolume()
  const volumeFor = useCallback(
    (identity: string) => (muted ? 0 : (level / 100) * (identity === sourceIdentity ? sourceLevel / 100 : 1)),
    [level, muted, sourceIdentity, sourceLevel],
  )
  return <EventLiveKitAudioRenderer volumeFor={volumeFor} />
})

interface WatchStageProps {
  eventId: string
  route: ViewerPayload['route']
  /** Every course of the event (the map draws each of several in its own colour, with a legend). */
  routes: ViewerPayload['routes']
  /** The world layer's elements (`useWorldLayer`), for the play world's markers. */
  worldElements: readonly WorldElement[]
  /** The event has something to map (`useHasMap`): the swap card and the map, or "No course map" in the phone's Map tab. */
  hasMap: boolean
  /** The event has sources to list beside the watched one (`useHasStrip`): the desktop keeps the strip's room under the canvas. */
  hasStrip: boolean
  /** The event's cameras and Live shows, offline ones included, for the canvas's and the grid's empty states. */
  cameraCount: CameraCount
  /** The event has a Live show, live or not: the desktop waits for the room before turning to the map. */
  hasLiveShow: boolean
  /** Where the event is in its schedule: with nothing live, the canvas and the grid warm up, call a lull a quiet stretch, or say all finished. */
  phase: EventPhase
  /** The room's connection, for the canvas's waiting states (`stopped` once nothing tries again). */
  connection: CanvasConnection
  screen: WatchScreenState
  /** A host's map as the stage's call (`useHostMapView`): followed here, or this tab's own, sent from here. */
  hostMap: HostMapView
  host: PublicProfile
  /** This tab's LiveKit identity, for its own face. */
  localIdentity: string | null
  /**
   * A dock shows (the host's, or a guest's): the desktop keeps a row for it between the canvas and the strip, wherever
   * it is placed.
   */
  docked: boolean
  /**
   * The dock's slot (the page renders the dock into a node of its own), under the canvas: on the desktop, on its side
   * and over the full map. Null where the page places it elsewhere (a phone's info line) or there is none.
   */
  dockSlot: ReactNode
  /** The stage on screen, when this viewer may invite people on it: the + face (the dock's Invite to stage is the page's). */
  inviteStageId: string | null
  /** The stage on screen, when this viewer hosts it: a guest's face gets Remove from stage. */
  hostStageId: string | null
  layout: WatchLayout
  /**
   * The map is on screen: the desktop's (in the canvas or its card), or the phone's Map tab while it is open. Behind
   * another tab, or on a phone on its side, it stays mounted out of sight.
   */
  mapShown: boolean
  /** The map fills the screen (a phone's full map, the map on its side): the canvas is a small player over it. */
  pip: boolean
  /** The small player was tapped, or a camera opened on the map under it: back to the video. */
  onPipOpen(): void
  /** A camera was picked in the strip (the page watches it; on a phone's side, back to the video). */
  onPickCamera(identity: string): void
  /** Where the camera strip and the map render; the desktop places them here, the phone in its panels. */
  stripNode: HTMLElement
  mapNode: HTMLElement
  /** The event's tracked athletes (`useEventAthletes`, the page's): the map's dots, polled while it shows. */
  athletes: EventAthletes
  /**
   * Show this athlete on the map (a host's Show on map): the map takes the desktop's canvas (the phone's tab is the
   * page's), on the play world, flying to them with their card open. Each key once.
   */
  athleteFocus: { id: string; key: number } | null
  /** The stage's broadcast elements over the canvas (`BroadcastLayer`), or null while no host is live on it. */
  broadcast: ReactNode
}

/** The small player's name: what tapping it does. */
const PIP_LABEL = 'Back to video'

/** The stage's reactions over the canvas: one element for every render, so the layer never remounts. */
const reactionLayer = <ReactionLayer />

/** A board's rows on a phone on its side: the canvas is wide but short. */
const LANDSCAPE_BOARD_ROWS = 5

/**
 * How long the room has to have been connected before the desktop of an event with a Live show opens on the map: a Live
 * show already live is listed a moment after the room connects, and the map must never flash in front of it. An event
 * without one opens on the map at once.
 */
const OPENING_SETTLE_MS = 1000

/** How recently a touch on the map keeps a host's map call from moving it under the viewer's finger. */
const MAP_TOUCH_GRACE_MS = 3000

/**
 * The stage: the canvas (the watched camera, the map in the swap card, the faces), the dock's slot and the camera
 * strip. Memoised, and every prop is stable while what it shows is (`useWatchScreen` returns the same object until
 * something in it changes), so the chat's renders pass it by.
 *
 * On the desktop the map sits in the canvas's swap card and the strip under the dock, in a row (no row, and no room kept
 * for one, when the event has one source or none: the strip never lists the watched one; an event that has more keeps
 * the room while its row is empty, so the canvas holds its size). On a phone the canvas has no card, and the strip (as
 * a grid) and the map live in the phone's panels; they render into their own nodes either way, so a layout change moves
 * them without a remount.
 *
 * Opening a camera on the map (a click, Enter, or a second tap on touch) jumps into it, and it becomes the watched camera
 * (the viewer's pick, a host's preview). With the map filling the desktop's canvas, the canvas turns to the camera,
 * growing it out of its sphere over the map (held still meanwhile: no follow ease), and the map goes back into the swap
 * card: the canvas's one video and the one map, no second player. The card (or Escape) goes back to the map, focus
 * returning to the watched camera's sphere, panned clear of the card if it sits under it. Watching another camera
 * meanwhile (Back to host, the strip) ends the jump. On a phone the canvas above the Map tab turns to the camera and the
 * map stays as it was (founder, 24 October 2026: the second tap makes it the main canvas camera, no view over the map);
 * from the full map, or the map on its side, the video comes back with it. The map hears the viewer's own connection:
 * while it is down, no camera on it shows live.
 *
 * Map first (founder, 23 October 2026: "When the live show is not live, the map should be the default view a user
 * lands on when they open the page"): unless something produced for viewers is live or on its way as a viewer arrives
 * on the desktop (the Live show, or a live host's call), the map takes the canvas on its own, and the watched camera
 * sits in the swap card, as the phone opens on its Map tab: as the page opens for an event without a Live show, and
 * once the room has connected and settled for one with a Live show. Cameras live meanwhile stay on the map, their pins
 * live. While the viewer hasn't touched that map (no press, wheel or key in it), the Live show going live comes onto
 * the canvas (as a swap: it has no place on the map), and so does a live host's call (growing out of the called
 * camera's pin when it has one, the same jump as opening it from the map). Once they explore it, swap or open a camera,
 * nothing moves under them. Until the map has something drawn, a loading wait stands over it full size (`MapLoading`).
 *
 * With nothing to map (no course and no located camera) there is no Mapbox world to look at: the desktop keeps no
 * swap card, and the phone's Map tab says "No course map". A map with nothing to frame for now (its located cameras
 * hidden offline) stays mounted under `MapWaiting`'s pin.
 */
const WatchStage = memo(function WatchStage({
  eventId,
  route,
  routes,
  worldElements,
  hasMap,
  hasStrip,
  cameraCount,
  hasLiveShow,
  phase,
  connection,
  screen,
  hostMap,
  host,
  localIdentity,
  docked,
  dockSlot,
  inviteStageId,
  hostStageId,
  layout,
  mapShown,
  pip,
  onPipOpen,
  onPickCamera,
  stripNode,
  mapNode,
  athletes,
  athleteFocus,
  broadcast,
}: WatchStageProps) {
  const desktop = layout === 'desktop'
  const [mode, setMode] = useState<'camera' | 'map'>('camera')
  // A camera jumped into from the full-size map, while the canvas shows it; the layout leaving the desktop ends it, and
  // so does the canvas watching another camera (Back to host, the strip, the host's call), so it never goes stale.
  // Until the jump settles (the camera has grown out of its sphere), the map keeps the canvas as the viewer left it.
  const [jump, setJump] = useState<(CanvasJump & { identity: string }) | null>(null)
  const [settled, setSettled] = useState<CanvasJump | null>(null)
  const pipCanvas = useMemo(() => (pip ? { label: PIP_LABEL, onOpen: onPipOpen } : null), [pip, onPipOpen])
  if (jump && (!desktop || screen.watchedIdentity !== jump.identity)) setJump(null)
  const mapHeld = jump !== null && settled !== jump
  // Another host's map is the stage's call (docs/features/follow-host-map.md): while the viewer follows, the map moves
  // with theirs.
  const mapHost = hostMap.sending ? null : hostMap.host
  const [following, setFollowing] = useState(false)
  const latest = useRef({ desktop, mode, jump, pip, watched: screen.watchedIdentity, mapCalled: mapHost !== null })
  useLayoutEffect(() => {
    latest.current = { desktop, mode, jump, pip, watched: screen.watchedIdentity, mapCalled: mapHost !== null }
  })
  // The opening (map first, or not) is decided once a visit; a swap or an opened camera before then decides it.
  const opened = useRef(false)
  // The map took the canvas on its own and the viewer hasn't touched it since: the Live show (or a host's call) going
  // live may take it back.
  const mapOnItsOwn = useRef(false)
  // The map has something drawn (CourseMap's `onLoadedChange`); until then, full size, MapLoading stands over it.
  const [mapLoaded, setMapLoaded] = useState(false)
  // A swap ends a jump: back on the map, focus returns to the watched camera's sphere (once the map is reachable
  // again), which CourseMap pans clear of the swap card if it sits under it.
  const onSwap = useCallback(() => {
    const { jump: jumped, watched, mode: was, mapCalled } = latest.current
    opened.current = true
    mapOnItsOwn.current = false
    setJump(null)
    setMode((current) => (current === 'camera' ? 'map' : 'camera'))
    // The host's map coming back onto the canvas follows them again; going into the card, it stops.
    setFollowing(was === 'camera' && mapCalled)
    if (jumped) focusCamera(mapNode, watched ?? jumped.identity)
  }, [mapNode])
  // What covers the full-size map's corner: the swap card, or the small player over a map filling the screen.
  const swapCard = useCallback(() => swapCardOver(mapNode), [mapNode])
  const smallPlayer = useCallback(() => document.querySelector<HTMLElement>('.watch-canvas[data-pip]'), [])
  const { select } = screen
  // A camera opened on the map (a click, Enter or a second tap, so from its preview): watch it, and with the map full
  // size on the desktop, jump the canvas into it, growing out of the sphere's frame where it is now. On a phone the
  // canvas above the Map tab turns to it; from a map filling the screen (the small player over it), back to the video.
  const onWatch = useCallback(
    (identity: string, frame: Element | null) => {
      select(identity)
      opened.current = true
      mapOnItsOwn.current = false
      setFollowing(false)
      const current = latest.current
      if (!current.desktop) {
        if (current.pip) onPipOpen()
        return
      }
      if (current.mode !== 'map') return
      setJump({ identity, from: jumpOrigin(frame) })
      setMode('camera')
    },
    [select, onPipOpen],
  )
  // Keyed on what the map draws, so a camera coming on its way doesn't hand it a new list: that would re-run its
  // follow ease.
  const mapKey = JSON.stringify(
    screen.cameras
      .filter((camera) => !isHostSource(camera))
      .map(({ identity, name, online, location }) => [identity, name, online, location?.latitude ?? null, location?.longitude ?? null]),
  )
  const mapCameras = useMemo<CourseMapCamera[]>(
    () =>
      (JSON.parse(mapKey) as [string, string, boolean, number | null, number | null][]).map(([identity, name, online, latitude, longitude]) => ({
        identity,
        name,
        online,
        coordinate: latitude === null || longitude === null ? null : { latitude, longitude },
      })),
    [mapKey],
  )
  // Nothing for the map to frame (`unframedMap`): the map stays mounted (one load, its mode kept) and hides under the
  // pin until a route or a located camera shows. In the swap card that map is a still picture, hidden from assistive
  // tech, so the pin says nothing; full size it sits in the canvas's ink on the desktop, the page's white on a phone.
  const unframed = unframedMap(route, screen.cameras)
  const mapInCard = desktop && mode === 'camera'

  // Map first. What the canvas opens on instead is something produced for viewers, live or on its way (listed, its
  // picture still coming, so the canvas waits for it instead of turning away): the Live show, or a call a live host is
  // making (to a camera, or their own Studio face, which is live by being on the stage). Cameras going live on their
  // own don't count: their pins do.
  const hostOnStage = screen.live.members.some((member) => member.role === 'host')
  const calledCamera = hostOnStage && screen.calledIdentity ? screen.cameras.find((camera) => camera.identity === screen.calledIdentity) : undefined
  const produced =
    screen.cameras.some((camera) => camera.sourceRole === 'programme' && (camera.online || camera.waiting)) ||
    (hostOnStage && screen.calledIdentity !== null && (calledCamera === undefined || calledCamera.online || calledCamera.waiting))
  const canOpenOnMap = desktop && hasMap && !unframed
  // An event without a Live show has nothing to wait for: the map takes the canvas as the page opens (before it paints),
  // without waiting for the room. A live host's call, once the room has it, still comes onto the canvas (below).
  useLayoutEffect(() => {
    if (opened.current || hasLiveShow || produced || !canOpenOnMap) return
    opened.current = true
    mapOnItsOwn.current = true
    setMode('map')
  }, [hasLiveShow, produced, canOpenOnMap])
  useEffect(() => {
    if (opened.current || connection !== 'connected') return
    if (produced || !canOpenOnMap) {
      opened.current = true
      return
    }
    const timer = window.setTimeout(() => {
      opened.current = true
      if (latest.current.mode !== 'camera') return
      mapOnItsOwn.current = true
      setMode('map')
    }, OPENING_SETTLE_MS)
    return () => window.clearTimeout(timer)
  }, [connection, produced, canOpenOnMap])
  // Touching the map while it fills the canvas is exploring it: from then on nothing takes the canvas from under them.
  // A host's map called meanwhile doesn't pull it from under a finger either (`touchedAt`).
  const touchedAt = useRef(Number.NEGATIVE_INFINITY)
  useEffect(() => {
    const explored = () => {
      touchedAt.current = performance.now()
      if (latest.current.mode === 'map') mapOnItsOwn.current = false
    }
    const events = ['pointerdown', 'wheel', 'keydown'] as const
    events.forEach((type) => mapNode.addEventListener(type, explored, { capture: true, passive: true }))
    return () => events.forEach((type) => mapNode.removeEventListener(type, explored, { capture: true }))
  }, [mapNode])
  // The Live show (or a live host's call) going live while the map holds the canvas on its own comes onto the canvas,
  // growing out of its place on the map when it has one: the world's pin when the world shows (the Mapbox spheres sit
  // hidden under it), else its sphere. One with no place on the map (the Live show, an unlocated camera) swaps in.
  const producedBefore = useRef(produced)
  useEffect(() => {
    const wasProduced = producedBefore.current
    producedBefore.current = produced
    if (wasProduced || !produced || !mapOnItsOwn.current) return
    mapOnItsOwn.current = false
    const { desktop: onDesktop, mode: current, watched } = latest.current
    if (!onDesktop || current !== 'map' || !watched) return
    const from = jumpOrigin(cameraFrame(mapNode.querySelector('.cm-world') ?? mapNode, watched))
    if (from) setJump({ identity: watched, from })
    setMode('camera')
  }, [produced, mapNode])
  // A host calling their map: it takes the canvas and follows them, unless the viewer chose a camera of their own or has a
  // finger on the map, who get Back to host (the canvas) or Follow (the map) instead. The call ending stops following,
  // and the map stays where it is.
  const mapHostIdentity = mapHost?.identity ?? null
  const seenMapHost = useRef<string | null>(null)
  const { picked } = screen
  useEffect(() => {
    if (seenMapHost.current === mapHostIdentity) return
    seenMapHost.current = mapHostIdentity
    if (!mapHostIdentity) {
      setFollowing(false)
      return
    }
    if (picked || performance.now() - touchedAt.current < MAP_TOUCH_GRACE_MS) return
    opened.current = true
    mapOnItsOwn.current = false
    setJump(null)
    setFollowing(true)
    if (latest.current.desktop) setMode('map')
  }, [mapHostIdentity, picked])
  const stopFollowing = useCallback(() => setFollowing(false), [])
  const { backToHost } = screen
  // Back to host while the host's map is the call: the map, following them.
  const backToHostMap = useCallback(() => {
    backToHost()
    opened.current = true
    mapOnItsOwn.current = false
    setJump(null)
    setMode('map')
    setFollowing(true)
  }, [backToHost])
  // A host's Show on map: their own map takes the canvas (CourseMap turns to the world and flies to the athlete).
  const focusKey = athleteFocus?.key
  useEffect(() => {
    if (focusKey === undefined) return
    opened.current = true
    mapOnItsOwn.current = false
    setJump(null)
    setFollowing(false)
    if (latest.current.desktop) setMode('map')
  }, [focusKey])
  const mapBack = desktop && mapHost !== null && mode === 'camera' && jump === null
  const { callCamera } = screen
  const stopShowingMap = useCallback(() => callCamera(null), [callCamera])
  // Keyed on the face's words, not its object: every room metadata update brings new member objects.
  const followName = mapHost ? nameOrHandle(mapHost.name, mapHost.handle, 'Host') : null
  const followHandle = mapHost?.handle ?? null
  const followHost = useMemo<PublicProfile | null>(
    () => (followName === null ? null : { kind: 'person', handle: followHandle, name: followName, avatar_url: null }),
    [followName, followHandle],
  )
  const { isHost } = screen
  const mapHostView = useMemo<CourseMapHostView | null>(() => {
    if (hostMap.sending) return { source: hostMap.source, overlay: <HostMapLive onStop={stopShowingMap} /> }
    return {
      // Any host's world offers its view, so Show my audience can call it once they're live (read only when sent).
      source: isHost ? hostMap.source : null,
      follow: followHost ? { store: hostMap.store, on: following, onBreak: stopFollowing, athlete: hostMap.athlete } : null,
      overlay: followHost ? <FollowHost host={followHost} following={following} onFollowingChange={setFollowing} /> : null,
    }
  }, [hostMap.sending, hostMap.source, hostMap.store, hostMap.athlete, isHost, followHost, following, stopFollowing, stopShowingMap])

  // The map fills the canvas (map mode, or held under a camera growing out of it) or its phone tab; otherwise it is the
  // swap card's light picture of the course (CourseMap's card), the same Mapbox map kept underneath.
  const mapFull = desktop ? mode === 'map' || mapHeld : true
  // Full size and nothing drawn yet: the loading wait stands over the map (not in the swap card; an unframed map has
  // its own wait).
  const mapLoading = mapFull && !mapLoaded && !unframed

  return (
    <div className="watch-screen__fit">
      <div
        className="watch-screen__stack"
        data-host={docked || undefined}
        data-strip={(desktop && hasStrip) || undefined}
        data-audience={(desktop && connection === 'connected') || undefined}
      >
        <WatchCanvas
          mode={mode}
          watched={screen.watched}
          cameraCount={cameraCount}
          phase={phase}
          connection={connection}
          calledIdentity={screen.calledIdentity}
          map={desktop && hasMap ? <MovableSlot node={mapNode} className="watch-fill" /> : undefined}
          course={hasCourse(route)}
          // Only while someone is on the stage as a face (a Studio face steps aside while the canvas shows it), Badger is
          // in, or the + face can invite, so an empty foot leaves a wait centred in the whole canvas.
          faces={
            screen.live.members.some((member) => !(member.studio && member.identity === screen.watchedIdentity)) ||
            screen.cohostIdentity !== null ||
            (inviteStageId && screen.live.members.length > 0) ? (
              <StageFaces
                members={screen.live.members}
                owner={screen.owner}
                localIdentity={localIdentity}
                connected={connection === 'connected'}
                inviteStageId={inviteStageId}
                hostStageId={hostStageId}
                watchedIdentity={screen.watchedIdentity}
                onWatch={screen.select}
                cohostIdentity={screen.cohostIdentity}
              />
            ) : undefined
          }
          // While there is something to hear: the watched camera's sound (even with its picture paused) or someone on the stage.
          sound={connection === 'connected' && screen.audioIdentities.length > 0 ? <WatchSound /> : undefined}
          fullscreen={canGoFullscreen() ? <FullscreenToggle /> : undefined}
          sourceOff={screen.sourceIdentity !== null && screen.sourceVolume === 0}
          overridden={screen.overridden || mapBack}
          host={host}
          onSwap={onSwap}
          onBackToHost={mapBack ? backToHostMap : screen.backToHost}
          label={screen.previewIdentity ? <PreviewLabel /> : undefined}
          jump={jump}
          onJumpSettled={setSettled}
          pip={pipCanvas}
          broadcast={broadcast}
          reactions={reactionLayer}
        />
        {desktop && connection === 'connected' && (
          <div className="watch-screen__crowd">
            <WatchBoundary name="audience" fallback={null} resetKey={eventId}>
              <WatchAudience members={screen.live.members} placement="stage" />
            </WatchBoundary>
          </div>
        )}
        {dockSlot}
        {desktop && hasStrip && <MovableSlot node={stripNode} />}
      </div>
      {createPortal(
        <CameraStrip
          cameras={screen.cameras}
          watchedIdentity={screen.watchedIdentity}
          calledIdentity={screen.calledIdentity}
          host={host}
          onSelect={onPickCamera}
          layout={desktop ? 'row' : 'grid'}
          compact={layout === 'landscape'}
          cameraCount={cameraCount}
          phase={phase}
          connection={connection}
          className="watch-screen__strip"
        />,
        stripNode,
      )}
      {createPortal(
        hasMap ? (
          <div className="watch-map__frame" data-blank={unframed || undefined} data-loading={mapLoading || undefined}>
            <div className="watch-map__course" {...(unframed ? UNFRAMED : {})}>
              <WatchBoundary name="map" fallback={(retry) => <MapUnavailable onRetry={retry} />} resetKey={eventId}>
                <CourseMap
                  eventId={eventId}
                  route={route}
                  routes={routes}
                  cameras={mapCameras}
                  worldElements={worldElements}
                  athletes={athletes}
                  selectedIdentity={screen.watchedIdentity}
                  calledIdentity={screen.calledIdentity}
                  followSelection={!screen.overridden}
                  holdView={jump !== null}
                  onWatch={onWatch}
                  interactive={mapFull}
                  // Upright on a phone the world's flights wait for the full map: the Map tab keeps its toggle and basemap.
                  worldControls={layout !== 'phone' || pip}
                  presentation={mapFull ? 'map' : 'card'}
                  shown={mapShown}
                  connectionStatus={mapConnectionStatus(connection)}
                  coveredBy={desktop ? swapCard : pip ? smallPlayer : undefined}
                  renderVideo={renderSphereVideo}
                  onLoadedChange={setMapLoaded}
                  logoPosition="bottom-right"
                  attribution="compact"
                  hostView={mapHostView}
                  athleteFocus={athleteFocus}
                />
              </WatchBoundary>
            </div>
            {/* Still on a phone, as the Cameras grid: the canvas above carries the wait's motion. */}
            {unframed && <MapWaiting inCard={mapInCard} tone={desktop ? 'inverse' : 'default'} phase={phase} still={!desktop} />}
            {mapLoading && <MapLoading tone={desktop ? 'inverse' : 'default'} />}
          </div>
        ) : (
          <NoCourseMap />
        ),
        mapNode,
      )}
    </div>
  )
})

/** What's wrong with the room, said above the canvas: the token's trouble, or a lost room; nothing while it plays. */
type RoomTroubleKind = WatchTokenError

/**
 * A failed refetch (after signing in, or a removal) can set the token's error while the older connection still plays,
 * so nothing shows while connected. A lost room that has a token says it can't connect (it is fetching a new one).
 */
function roomTroubleFrom({ connection, error }: WatchRoomStatus): RoomTroubleKind | null {
  if (connection === 'connected') return null
  return error ?? (connection === 'lost' ? 'connect_failed' : null)
}

/**
 * The room's trouble in one polite live region that is always there, so a notice that appears is announced (a region
 * that mounts with its text often isn't). The notice inside isn't a second region.
 */
function RoomTrouble({ trouble, onRetry }: { trouble: RoomTroubleKind | null; onRetry: () => void }) {
  return (
    <div className="watch-screen__status" aria-live="polite" aria-atomic="true">
      {trouble === 'busy' && (
        <Notice tone="warning" aria-live="off" className="watch-screen__notice">
          {BUSY}
        </Notice>
      )}
      {trouble === 'connect_failed' && (
        <Notice tone="error" aria-live="off" className="watch-screen__notice">
          {CONNECT_FAILED}
        </Notice>
      )}
      {trouble === 'unavailable' && (
        <Notice
          tone="error"
          aria-live="off"
          className="watch-screen__notice"
          action={
            <Button variant="secondary" size="compact" onClick={onRetry}>
              Try again
            </Button>
          }
        >
          {VIDEO_UNAVAILABLE}
        </Notice>
      )}
    </div>
  )
}

/** A part that failed to draw, as the chat's history failure: an error Notice with Try again, which draws it again. */
function PartFailed({ message, onRetry, className }: { message: string; onRetry: () => void; className: string }) {
  return (
    <Notice
      tone="error"
      className={className}
      action={
        <Button variant="secondary" size="compact" onClick={onRetry}>
          Try again
        </Button>
      }
    >
      {message}
    </Notice>
  )
}

/** The map's place when it fails to draw: the Notice on the map's surface (the swap card keeps the surface alone). */
function MapUnavailable({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="watch-map__failed">
      <PartFailed message={MAP_FAILED} onRetry={onRetry} className="watch-map__notice" />
    </div>
  )
}

/**
 * The chat's place when it fails to draw: the panel's surface with its head (on the desktop the only place naming the
 * host; upright on a phone, who's online alone, the info line above naming the host) and the Notice where the history
 * failure's goes. The head has a boundary of its own: when the head is what failed, the Notice stands alone.
 */
function ChatUnavailable({ onRetry, head }: { onRetry: () => void; head: ReactNode }) {
  const headingId = useId()
  return (
    <section className="watch-chat" aria-labelledby={headingId}>
      <h2 id={headingId} className="sr-only">
        Chat
      </h2>
      <WatchBoundary name="chat head" fallback={null}>
        {head}
      </WatchBoundary>
      <PartFailed message={CHAT_FAILED} onRetry={onRetry} className="watch-chat__notice" />
    </section>
  )
}

interface WatchChatHeadProps extends Omit<ChatHeadProps, 'onStage'> {
  /** The stage's seats (the room's metadata, the stage payload's before it). */
  members: readonly StageFace[]
  /** The room is connected: before then the room's word on the stage isn't in, so nothing is counted. */
  connected: boolean
}

/**
 * The chat's head with how many are on the stage, counted here rather than across the page (it changes with arrivals
 * and departures). It counts a seat only while its participant is in the room (`useInRoomCount`, this tab included): a
 * seat record outlives a closed tab by a minute or more, as the ring's rule. Memoised on plain props (the seats are the
 * room metadata's own array), so typing in the chat passes it by.
 */
const WatchChatHead = memo(function WatchChatHead({ members, connected, ...props }: WatchChatHeadProps) {
  const present = useInRoomCount(members.map((member) => member.identity))
  return <ChatHead onStage={connected ? present : 0} {...props} />
})

/**
 * Who's watching as an audience (`Audience`) with the reaction bar at its end: under the chat's head on a phone, under
 * the video on the desktop. Read here rather than across the page: it changes with every arrival and departure. The
 * stage's seats (the room metadata's own array) mark its live hosts. Nothing before the room connects. Memoised, so
 * typing in the chat passes it by.
 */
const WatchAudience = memo(function WatchAudience({ members, placement }: { members: readonly StageFace[]; placement: 'chat' | 'stage' }) {
  const online = useOnline(members)
  return online ? <Audience online={online} placement={placement} actions={<ReactionBar />} /> : null
})

/**
 * The phone's stage button with how many are on the stage, counted here as the chat's head counts it (a seat only while
 * its participant is in the connected room). Memoised on plain props.
 */
const WatchStageButton = memo(function WatchStageButton({
  members,
  connected,
  ...props
}: Omit<StageButtonProps, 'onStage'> & { members: readonly StageFace[]; connected: boolean }) {
  const present = useInRoomCount(members.map((member) => member.identity))
  return <StageButton onStage={connected ? present : 0} {...props} />
})

/**
 * The phone's info line (`WatchInfo`) with its button named for what it opens: the Stages sheet, named as the stage
 * button and the chat's head name the stage on screen (how many are on it counted here, a seat only while its
 * participant is in the connected room), or the event's details. Memoised on plain props.
 */
const WatchInfoLine = memo(function WatchInfoLine({
  members,
  connected,
  switchable,
  ...props
}: Omit<WatchInfoProps, 'label' | 'hostLabel'> & { members: readonly StageFace[]; connected: boolean; switchable: boolean }) {
  const present = useInRoomCount(members.map((member) => member.identity))
  const label = switchable
    ? `${props.title}, ${stageLabel(props.host.name, connected ? present : 0)}. Change stage`
    : `${props.title}, event details`
  // The chat has no head upright, so with one stage the avatar says who hosts it, as the head did (the button names
  // the stage, with its host, otherwise).
  const hostLabel = switchable
    ? undefined
    : `Hosted by ${props.host.name}${props.host.verified ? ', Verified' : ''}${props.hostLive ? ', live on stage' : ''}`
  return <WatchInfo label={label} hostLabel={hostLabel} {...props} />
})

/** The host's dock with the others watching (for the camera check), counted here rather than across the page. */
function WatchHostDock(props: Omit<HostDockProps, 'canHost' | 'audienceCount'>) {
  const { othersWatching } = useAudience()
  return <HostDock canHost audienceCount={othersWatching} {...props} />
}

/** A guest's camera check with the others watching (who will see and hear them), counted here as for the host's. */
function WatchGuestCheck(props: Omit<CameraCheckSheetProps, 'audienceCount'>) {
  const { othersWatching } = useAudience()
  return <CameraCheckSheet audienceCount={othersWatching} {...props} />
}

/** The site's header and footer, as every other page has them; memoised, so the screen's renders (typing) pass them by. */
const SiteTop = memo(SiteHeader)
const SiteFoot = memo(SiteFooter)

/**
 * What the shell frames: the room, its skeleton (`loading`: the payload or the stage on its way), or a `message` (not
 * found, a failed load, a stage not there yet). The room and its skeleton fill the screen; a message flows.
 */
/** `preshow`: the pre-show fills the fold as the room does, under the header (never over it), with the footer below. */
type WatchShellView = 'room' | 'loading' | 'message' | 'preshow'

/** What the content asks of the site's header and footer (`useWatchShellSite`). */
interface WatchShellSite {
  /** Asked before the header or footer leaves (SiteHeader's and SiteFooter's `beforeLeave`): the room's leave guard. */
  beforeLeave?: BeforeLeave
  /** The header's Log in opens the room's sign-in sheet; without it, Log in goes to the login page and back here. */
  onLogIn?: () => void
  /** What Edit profile's way back calls this page: the event's name. */
  profileLabel?: string | null
}

const NO_SITE: WatchShellSite = {}

interface WatchShellState {
  /** The page's layout: the screen's, held while the viewer types on it; a message on a phone's side lays out by width. */
  layout: WatchLayout
  /** The viewer is typing in a field on the screen (the chat's composer): the layout holds, and so does the phone's tab. */
  typing: boolean
  setView(view: WatchShellView): void
  setSite(site: WatchShellSite): void
  /** The room fills the screen by itself (the phone's full map): no site header, as on a phone's side. */
  setImmersive(immersive: boolean): void
  /** The event's logo in the site's header in place of StreamBridge's (SiteHeader's `eventLogo`), or none. */
  setEventLogo(logo: SiteEventLogo | null): void
}

/**
 * Typing on an upright phone, the canvas stays above the chat while the visible area leaves the chat this much room
 * under it (the composer and about three messages); with less (a small phone, its keyboard up) it steps aside.
 */
const TYPING_CHAT_ROOM = 180

/** The canvas's share of an upright phone's width: full-bleed at 16:9. */
const PHONE_CANVAS_RATIO = 9 / 16

const WatchShellContext = createContext<WatchShellState | null>(null)

function useShellContext(): WatchShellState {
  const shell = useContext(WatchShellContext)
  if (!shell) throw new Error('The watch screen renders inside WatchShell')
  return shell
}

/**
 * The shell around this content: its layout, and `typing`. `view` tells the shell what the content is, so it knows
 * whether the page fills the screen and where its footer goes; declared before paint, so nothing shows in between.
 */
function useWatchShell(view: WatchShellView): WatchShellState {
  const shell = useShellContext()
  const { setView } = shell
  useLayoutEffect(() => setView(view), [setView, view])
  return shell
}

/**
 * Hands the site's header and footer what this content asks of them (the room's leave guard and sign-in, the event's
 * name for Edit profile's way back) while it is on screen, before paint. Keep the functions stable: the header and
 * footer are memoised.
 */
function useWatchShellSite({ beforeLeave, onLogIn, profileLabel = null }: WatchShellSite) {
  const { setSite } = useShellContext()
  useLayoutEffect(() => {
    setSite({ beforeLeave, onLogIn, profileLabel })
    return () => setSite(NO_SITE)
  }, [setSite, beforeLeave, onLogIn, profileLabel])
}

/** Puts the event's logo in the site's header in place of StreamBridge's while this page is on screen, before paint.
 *  Keep it stable: the header is memoised. */
function useWatchShellEventLogo(logo: SiteEventLogo | null) {
  const { setEventLogo } = useShellContext()
  useLayoutEffect(() => {
    setEventLogo(logo)
    return () => setEventLogo(null)
  }, [setEventLogo, logo])
}

/** Tells the shell the room fills the screen by itself (the phone's full map) while `immersive`, before paint. */
function useWatchShellImmersive(immersive: boolean) {
  const { setImmersive } = useShellContext()
  useLayoutEffect(() => {
    setImmersive(immersive)
    return () => setImmersive(false)
  }, [setImmersive, immersive])
}

/**
 * The site's frame around the watch screen (the founder: the same header, navigation and footer as the rest of the
 * site): `SiteHeader` on top (the logo, the main navigation with Docs, the account menu or Log in; a drawer on phones)
 * and `SiteFooter` below, as the home page has it. Upright on a phone the room and its skeleton put the video first (the
 * founder, 21 October 2026: Twitch and YouTube Live on a phone): the same header becomes its `overlay` over the video's
 * top edge (the compact mark home, the account menu and the drawer; `data-overlay`), still the page's one banner before
 * its `main`, with the same leave guard, Log in and Edit profile. An event that asks for its logo there has it top left
 * in place of StreamBridge's (`useWatchShellEventLogo`), in the row and over the video. The header's row spans the
 * screen's width (`fullWidth`), so the logo lines up with the bar and the canvas on any screen. Both ask the content before they leave, and the header's Log in
 * and Edit profile's way back are the content's (`useWatchShellSite`): in the room, "End your live?" while live, and
 * Log in opens the sign-in sheet. Edit profile comes back to this page, whatever step it is at.
 *
 * The route renders it once around every step of the event's page (the payload's skeleton, the stage's, the room, or
 * why not), so the header and footer mount once: a drawer or menu open while the event loads stays open. The content
 * says what it is (`useWatchShell`). The room and its skeleton fill the viewport under the header, the footer below
 * the fold; the phone's room puts the footer at the end of its Cameras panel instead. A phone on its side shows the
 * room (or its skeleton) alone, with no header or footer: the video fills the screen; so does the phone's full map
 * (`useWatchShellImmersive`). A message there (no video) keeps both and lays out by the screen's width, as any page.
 *
 * Typing in the room on a phone (upright or on its side), the fold pins itself to the visible area above the keyboard
 * (`useVisibleArea`, `data-typing`): watch.css sizes it to that area and moves it with it, so the page doesn't slide
 * away under the keyboard and the composer sits on top of it, the messages above. Meanwhile the site's header, the
 * event's bar, the switcher and the host's dock step aside (still mounted), and upright the canvas does too when it
 * would leave the chat less than `TYPING_CHAT_ROOM` (`data-typing="tight"`). Once the field lets go, all of it comes
 * back as it was.
 */
export function WatchShell({ children }: { children: ReactNode }) {
  const [view, setView] = useState<WatchShellView>('loading')
  const typing = useTypingOnScreen()
  const screenLayout = useHeld(useWatchLayout(), typing)
  const phone = useMediaQuery(PHONE_QUERY)
  // A message or the pre-show lays out by width on a phone's side: neither is video to fill the screen with.
  const plain = view === 'message' || view === 'preshow'
  const layout: WatchLayout = plain && screenLayout === 'landscape' ? (phone ? 'phone' : 'desktop') : screenLayout
  const [immersive, setImmersive] = useState(false)
  const room = view === 'room'
  const bare = layout === 'landscape' || (room && immersive)
  // Upright on a phone, the room and its skeleton put the video first: the header floats over its top edge.
  const overlay = !bare && layout === 'phone' && !plain
  const footer = !bare && (view !== 'room' || layout === 'desktop')
  const pinned = typing && room && layout !== 'desktop'
  const visible = useVisibleArea(pinned)
  const tight = pinned && layout === 'phone' && visible !== null && visible.height - window.innerWidth * PHONE_CANVAS_RATIO < TYPING_CHAT_ROOM
  const [site, setSite] = useState<WatchShellSite>(NO_SITE)
  const [eventLogo, setEventLogo] = useState<SiteEventLogo | null>(null)
  const shell = useMemo(() => ({ layout, typing, setView, setSite, setImmersive, setEventLogo }), [layout, typing])
  const { pathname, search } = useLocation()
  const here = `${pathname}${search}`
  const profileReturn = useMemo(() => ({ to: here, ...(site.profileLabel ? { label: site.profileLabel } : {}) }), [here, site.profileLabel])
  const visibleArea = visible
    ? ({ '--watch-visible-height': `${visible.height}px`, '--watch-visible-top': `${visible.top}px` } as CSSProperties)
    : undefined
  return (
    <WatchShellContext.Provider value={shell}>
      <div className="watch-page" data-layout={layout} data-overlay={overlay || undefined} data-typing={pinned ? (tight ? 'tight' : '') : undefined}>
        <div className="watch-page__fold" data-fill={view !== 'message' || undefined} style={visibleArea}>
          {!bare && (
            <SiteTop
              fullWidth
              overlay={overlay}
              className="watch-page__header"
              beforeLeave={site.beforeLeave}
              onLogIn={site.onLogIn}
              profileReturn={profileReturn}
              eventLogo={eventLogo ?? undefined}
            />
          )}
          {children}
        </div>
        {footer && <SiteFoot beforeLeave={site.beforeLeave} />}
      </div>
    </WatchShellContext.Provider>
  )
}

/**
 * The page's frame outside the room, in the shell: the event's bar and what's loading, or why it isn't. `title` is the
 * event's name, left out while it loads (a placeholder shows), or null where there is none (an unknown event, a failed
 * load): the body then carries the page's heading. `fill` for the skeleton, which sizes itself to the screen as the
 * room does; upright on a phone it puts the video first as the room does (the name stays the page's hidden heading,
 * the skeleton has the info line's shape).
 */
export function WatchScreenFrame({
  title,
  distanceKm,
  routes,
  fill = false,
  children,
}: {
  title?: string | null
  distanceKm?: number | null
  routes?: ViewerPayload['routes']
  fill?: boolean
  children: ReactNode
}) {
  const { layout } = useWatchShell(fill ? 'loading' : 'message')
  useWatchShellSite({ profileLabel: title })
  return (
    <main className="watch-screen" data-layout={layout}>
      {/* Upright on a phone the skeleton puts the video first, as the room does: the name is the info line's shape. */}
      <WatchEventBar title={title} distanceKm={distanceKm} routes={routes} layout={layout} bare={fill && layout === 'phone'} />
      {children}
    </main>
  )
}

interface WatchEventBarProps {
  title?: string | null
  live?: boolean
  /** Shows how many are watching (LiveKit's watchers, once connected). Inside the room only. */
  audience?: boolean
  /** The course's length in kilometres (the payload's primary route), after the audience; nothing without one. */
  distanceKm?: number | null
  /** Every course of the event: with two or more, the bar shows the range their lengths span ("10–42 km") instead. */
  routes?: ViewerPayload['routes']
  /**
   * Upright on a phone the room has the info line instead, and a message's bar takes two rows (watch.css); on its side
   * the video fills the screen, with no bar.
   */
  layout: WatchLayout
  /** The viewer is live on the stage (or going live): the Live badge holds still (the live workspace stays quiet). */
  onStage?: boolean
  /** The stage switcher, after the meta (the desktop with more than one stage on the event). */
  stages?: ReactNode
  /**
   * No bar, as on a phone's side: the room fills the screen by itself (a phone's full map), or the skeleton puts the
   * video first upright on a phone.
   */
  bare?: boolean
}

/**
 * The event's bar, directly under the site's header: the event's name (the page's h1) and the prototype's meta line,
 * "● Live · 👁 1.6K watching · 86.4 km": the Live badge (only while a camera has video, pulsing for the audience), the
 * eye, the count and "watching", and the course's length. The dots are for the eye; screen readers hear commas and the
 * unit in full. The meta's line is kept from the start, so the bar doesn't grow when the room connects. The host isn't
 * here: the chat's head names them (the desktop's column, the phone's Chat tab). Memoised on plain props; the audience
 * re-renders the meta alone. A phone on its side has no bar, nor a phone's full map: the event's name stays as the
 * page's heading, visually hidden. Upright on a phone the room has no bar either: the info line under the video
 * (`WatchInfo`) carries the name and the same meta. With more than one stage on the event, the desktop's bar ends with
 * the stage switcher (the prototype's header); the bar's meta stays the event's (its audience is everyone's). Exported
 * for its story.
 */
export const WatchEventBar = memo(function WatchEventBar({
  title,
  live = false,
  audience = false,
  distanceKm = null,
  routes,
  layout,
  onStage = false,
  stages = null,
  bare = false,
}: WatchEventBarProps) {
  if (title === null) return null
  if (layout === 'landscape' || bare) return title ? <h1 className="sr-only">{title}</h1> : null
  const length = courseLengthOf(routes, distanceKm)
  return (
    <div className="watch-screen__bar">
      {title ? <h1 className="watch-screen__title">{title}</h1> : <Skeleton className="watch-screen__skeleton-title" />}
      {audience ? (
        <WatchMetaWithAudience live={live} length={length} onStage={onStage} />
      ) : (
        <WatchMeta live={live} audience={null} length={length} onStage={onStage} />
      )}
      {stages}
    </div>
  )
})

type CourseLength = ReturnType<typeof formatCourseLength> | ReturnType<typeof formatCourseRange>

/** The courses' lengths as the meta says them: the range several courses span, else the primary route's length. */
function courseLengthOf(routes: ViewerPayload['routes'], distanceKm: number | null | undefined): CourseLength {
  return formatCourseRange((routes ?? []).map(courseKm)) ?? formatCourseLength(distanceKm)
}

interface WatchMetaProps {
  live: boolean
  /** How many are watching; null before the room connects (or outside it), and nothing shows. */
  audience: number | null
  length: CourseLength
  onStage: boolean
}

/** The bar's meta with the room's audience, counted here: it changes with every arrival and departure. */
function WatchMetaWithAudience(props: Omit<WatchMetaProps, 'audience'>) {
  const { audience } = useAudience()
  return <WatchMeta audience={audience} {...props} />
}

/** A dot between two of the meta's items, with a comma for screen readers in its place. */
function MetaDot() {
  return (
    <>
      <span className="sr-only">,</span>
      <span className="watch-screen__dot" aria-hidden="true">
        ·
      </span>
    </>
  )
}

function WatchMeta({ live, audience, length, onStage }: WatchMetaProps) {
  const items: ReactNode[] = []
  // It pulses for the audience; while this tab is on the stage the live workspace stays quiet (/design).
  if (live) {
    items.push(
      <Badge key="live" tone="live" pulse={!onStage}>
        Live
      </Badge>,
    )
  }
  if (audience !== null) {
    items.push(
      <span key="audience" className="watch-screen__audience">
        <Eye aria-hidden="true" />
        {formatAudience(audience)} watching
      </span>,
    )
  }
  if (length) {
    items.push(
      <span key="length" className="watch-screen__distance">
        <span aria-hidden="true">{length.short}</span>
        <span className="sr-only">
          {'several' in length ? 'Courses' : 'Course'} {length.long}
        </span>
      </span>,
    )
  }
  return (
    <p className="watch-screen__meta">
      {items.flatMap((item, index) => (index === 0 ? [item] : [<MetaDot key={`dot-${index}`} />, item]))}
    </p>
  )
}

/**
 * The canvas and chat while the event loads; upright on a phone, with the info line's shape between them (the avatar,
 * the name and the meta), so nothing moves when the room takes its place.
 */
export function WatchScreenSkeleton() {
  const phone = useContext(WatchShellContext)?.layout === 'phone'
  return (
    <SkeletonGroup label="Loading the event" className="watch-screen__body">
      <div className="watch-screen__watch">
        <Skeleton shape="media" className="watch-screen__skeleton-canvas" />
      </div>
      {phone && (
        <div className="watch-info-row">
          <div className="watch-info">
            <Skeleton shape="circle" className="watch-info__avatar" />
            <div className="watch-info__text">
              <Skeleton className="watch-screen__skeleton-title" />
              <Skeleton className="watch-info__skeleton-meta" />
            </div>
          </div>
        </div>
      )}
      <Skeleton shape="panel" className="watch-screen__chat" />
    </SkeletonGroup>
  )
}

/** Nothing to watch: why, and what to do next. `heading` is the page's visually hidden `h1` when the frame has no title. */
export function WatchScreenMessage({ heading, children }: { heading?: string; children: ReactNode }) {
  return (
    <div className="watch-screen__message">
      {heading && <h1 className="sr-only">{heading}</h1>}
      {children}
    </div>
  )
}

/** The event (or its stage) didn't load: say so, with a retry. `heading` when the frame has no title (no event yet). */
export function WatchScreenLoadFailed({ onRetry, heading = false }: { onRetry: () => void; heading?: boolean }) {
  return (
    <WatchScreenMessage heading={heading ? LOAD_FAILED_HEADING : undefined}>
      <Notice tone="error" action={<Button onClick={onRetry}>Try again</Button>}>
        {LOAD_FAILED}
      </Notice>
    </WatchScreenMessage>
  )
}
